import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogPortal,
} from '@/components/ui/dialog';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Home,
  Plus,
  Search,
  Settings,
  FileText,
  MessageSquare,
  DollarSign,
  ChevronRight,
  Sparkles,
  BookOpen,
  ClipboardCheck,
  ExternalLink,
  CircleDot,
  ArrowUp,
  Paperclip,
  Download,
  FileIcon,
  CheckCircle2,
  CircleDashed,
  Clock,
  Share2,
  MoreHorizontal,
  TrendingUp,
  TrendingDown,
  Minus,
  LayoutGrid,
  MessageCircle,
  ArrowRight,
  PanelLeftClose,
  PanelLeftOpen,
  AlertCircle,
  Loader2,
  Hammer,
  Lock,
  XCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  currentProperty,
  formatMoney,
  Severity,
  Category,
  FINDING_CATEGORIES,
} from './mockData';
import {
  ShieldAlert,
  Building2,
  Droplets,
  Bug,
  Zap,
  Scale,
  Trees,
  Thermometer,
  Leaf,
  Home as HomeIcon,
} from 'lucide-react';
import { PreviewNav } from './PreviewNav';
import { InlineEdit, useCanEdit } from './InlineEdit';
import { ProfileMenu } from './ProfileMenu';

const PDFSidePanel = lazy(() =>
  import('@/components/property/PDFSidePanel').then(m => ({ default: m.PDFSidePanel }))
);

const SAMPLE_PDF_URL = '/sample-disclosure.pdf';

function parseCitedPage(cited: string | undefined): number {
  if (!cited) return 1;
  const m = cited.match(/page\s*(\d+)/i);
  return m ? Math.max(1, parseInt(m[1], 10)) : 1;
}

const sevStyles: Record<Severity, { bg: string; text: string; label: string; dot: string; border: string }> = {
  critical: { bg: 'bg-brand-critical-bg', text: 'text-brand-critical', label: 'Critical', dot: 'bg-brand-critical', border: 'border-brand-critical/30' },
  high: { bg: 'bg-brand-high-bg', text: 'text-brand-high', label: 'High', dot: 'bg-brand-high', border: 'border-brand-high/30' },
  moderate: { bg: 'bg-brand-moderate-bg', text: 'text-brand-moderate', label: 'Moderate', dot: 'bg-brand-moderate', border: 'border-brand-moderate/30' },
  info: { bg: 'bg-brand-info-bg', text: 'text-brand-info', label: 'Routine', dot: 'bg-brand-info', border: 'border-brand-info/30' },
};

const CATEGORY_ICONS: Record<Category, React.ComponentType<{ className?: string }>> = {
  safety: ShieldAlert,
  structure: Building2,
  water: Droplets,
  pest: Bug,
  electrical: Zap,
  legal: Scale,
  exterior: Trees,
  roof: HomeIcon,
  hvac: Thermometer,
  environmental: Leaf,
};

type Section = 'overview' | 'findings' | 'documents' | 'messages' | 'offers' | 'share' | 'permits';
type Mode = 'dossier' | 'chat';

export type PreviewCRole = 'seller' | 'buyer' | 'brokerage';

export interface PreviewCProps {
  role?: PreviewCRole;
  walkthroughMode?: boolean;
  // When the walkthrough shell owns routing it passes these to take over
  // section state. When omitted, PreviewC falls back to useParams/useNavigate.
  section?: Section;
  onSectionChange?: (s: Section) => void;
  onBackToListings?: () => void;
  // Optional controlled mode (dossier ↔ chat). When omitted, falls back to
  // local state — preserves the standalone /preview/c behavior. Used by the
  // tour to drive the user into Chat mode for the "decrease liability" step.
  mode?: Mode;
  onModeChange?: (m: Mode) => void;
}

export default function PreviewC({
  role = 'seller',
  walkthroughMode = false,
  section: sectionProp,
  onSectionChange,
  onBackToListings: _onBackToListings,
  mode: modeProp,
  onModeChange,
}: PreviewCProps = {}) {
  const params = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const [modeLocal, setModeLocal] = useState<Mode>('dossier');
  const mode: Mode = modeProp ?? modeLocal;
  const setMode = (m: Mode) => {
    if (onModeChange) onModeChange(m);
    else setModeLocal(m);
  };
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Right rail removed — Ask Closer lives in Chat mode (header toggle) only

  const activeSection: Section = sectionProp ?? ((params.section as Section) || 'overview');
  const setSection = (s: Section) => {
    if (onSectionChange) onSectionChange(s);
    else navigate(`/preview/c/${s}`);
  };

  const commonProps = {
    activeSection,
    setSection,
    sidebarCollapsed,
    toggleSidebar: () => setSidebarCollapsed(v => !v),
  };

  return (
    <div className={`min-h-screen font-brand text-brand-ink ${walkthroughMode ? 'bg-transparent' : 'bg-brand-paper-warm'}`}>
      {!walkthroughMode && <PreviewNav />}

      {!walkthroughMode && <TopBar mode={mode} setMode={setMode} />}

      {walkthroughMode && <WalkthroughModeToggle mode={mode} setMode={setMode} role={role} />}

      {mode === 'chat' ? <ChatMode {...commonProps} /> : <DossierMode {...commonProps} />}
    </div>
  );
}

/* Mode toggle (Dossier / Chat) when in walkthrough mode — sits above the
   hero band because the landing-style header has already been rendered by the
   walkthrough shell. Mirrors the toggle styling from TopBar. */
function WalkthroughModeToggle({
  mode,
  setMode,
  role: _role,
}: {
  mode: Mode;
  setMode: (m: Mode) => void;
  role: PreviewCRole;
}) {
  return (
    <div className="max-w-[1440px] mx-auto px-4 md:px-6 pt-3 md:pt-4">
      <div className="inline-flex items-center gap-1 bg-white/80 backdrop-blur rounded-pill p-0.5 border border-black/5 shadow-sm">
        <button
          data-testid="dossier-mode-toggle-dossier"
          onClick={() => setMode('dossier')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-pill font-meta text-xs transition-colors ${
            mode === 'dossier'
              ? 'bg-brand-ink text-white font-semibold'
              : 'text-brand-text-3 hover:text-brand-ink'
          }`}
        >
          <LayoutGrid className="w-3 h-3" /> Dossier
        </button>
        <button
          data-testid="dossier-mode-toggle-chat"
          onClick={() => setMode('chat')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-pill font-meta text-xs transition-colors ${
            mode === 'chat'
              ? 'bg-brand-ink text-white font-semibold'
              : 'text-brand-text-3 hover:text-brand-ink'
          }`}
        >
          <MessageCircle className="w-3 h-3" /> Chat
        </button>
      </div>
    </div>
  );
}

type PanelProps = {
  activeSection: Section;
  setSection: (s: Section) => void;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
};

function gridCols(leftCollapsed: boolean) {
  const left = leftCollapsed ? '64px' : '240px';
  return `${left} 1fr`;
}

function useIsDesktop(breakpoint = 1024) {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia(`(min-width: ${breakpoint}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isDesktop;
}

function MobileSectionPills({
  activeSection,
  setSection,
}: {
  activeSection: Section;
  setSection: (s: Section) => void;
}) {
  const p = currentProperty;
  const items: { key: Section; label: string; count: number | null; alert?: boolean }[] = [
    { key: 'messages', label: 'Messages', count: p.qa.length },
    { key: 'offers', label: 'Offers', count: p.offers.length },
    { key: 'share', label: 'Share', count: 3, alert: true },
    { key: 'overview', label: 'Overview', count: null },
    { key: 'findings', label: 'Findings', count: p.findings.length },
    { key: 'permits', label: 'Permits', count: 18 },
    { key: 'documents', label: 'Documents', count: p.docs_count },
  ];
  return (
    <div className="lg:hidden sticky top-14 z-30 bg-brand-paper-warm/95 backdrop-blur border-b border-black/5">
      <div className="flex gap-1.5 overflow-x-auto px-4 py-2.5 no-scrollbar">
        {items.map(item => {
          const isActive = activeSection === item.key;
          return (
            <button
              key={item.key}
              onClick={() => setSection(item.key)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-pill font-meta text-xs transition-colors whitespace-nowrap ${
                isActive
                  ? 'bg-brand-ink text-white font-semibold'
                  : 'bg-white text-brand-text-2 border border-black/5'
              }`}
            >
              {item.label}
              {item.count !== null && item.count > 0 && (
                <span
                  className={`${
                    isActive
                      ? 'text-white/70'
                      : item.alert
                        ? 'bg-brand-critical text-white rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none'
                        : 'text-brand-text-3'
                  }`}
                >
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   TOP BAR — shared across dossier + chat modes
   ============================================================ */

function TopBar({ mode, setMode }: { mode: Mode; setMode: (m: Mode) => void }) {
  return (
    <header className="border-b border-black/5 bg-white sticky top-0 z-40">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 h-14 flex items-center gap-3 md:gap-6">
        <div className="flex items-center gap-2 md:mr-4 shrink-0">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-sky to-brand-cream flex items-center justify-center">
            <Sparkles className="w-3 h-3 text-brand-ink" />
          </div>
          <span className="font-display text-lg font-semibold">Discloser</span>
        </div>

        <nav className="hidden sm:flex items-center gap-1 font-meta text-xs">
          <Link to="/preview/c-home">
            <NavLink>
              <Home className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Properties</span>
            </NavLink>
          </Link>
          <Link to="/preview/c-add">
            <NavLink>
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Add</span>
            </NavLink>
          </Link>
          <Link to="/preview/c-settings">
            <NavLink>
              <Settings className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Settings</span>
            </NavLink>
          </Link>
        </nav>

        <div className="hidden md:block flex-1 max-w-md mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-text-3" />
            <Input
              placeholder="Search properties, findings, citations..."
              className="h-9 pl-9 rounded-pill bg-brand-paper-warm border-black/5 font-meta text-xs"
            />
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 font-meta text-[10px] text-brand-text-3 bg-white border border-black/10 px-1.5 py-0.5 rounded">⌘K</kbd>
          </div>
        </div>

        <div className="flex-1 md:hidden" />

        {/* Mode toggle */}
        <div className="flex items-center gap-1 bg-brand-paper-warm rounded-pill p-0.5 border border-black/5">
          <button
            onClick={() => setMode('dossier')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-pill font-meta text-xs transition-colors ${
              mode === 'dossier' ? 'bg-white text-brand-ink shadow-sm font-semibold' : 'text-brand-text-3 hover:text-brand-ink'
            }`}
          >
            <LayoutGrid className="w-3 h-3" /> Dossier
          </button>
          <button
            onClick={() => setMode('chat')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-pill font-meta text-xs transition-colors ${
              mode === 'chat' ? 'bg-white text-brand-ink shadow-sm font-semibold' : 'text-brand-text-3 hover:text-brand-ink'
            }`}
          >
            <MessageCircle className="w-3 h-3" /> Chat
          </button>
        </div>

        <ProfileMenu />
      </div>
    </header>
  );
}

/* ============================================================
   HERO BAND — property context, always visible
   ============================================================ */

function HeroBand() {
  const p = currentProperty;
  return (
    <div className="bg-gradient-to-b from-brand-sky/35 via-brand-cream/20 to-transparent border-b border-black/5">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-5 md:py-7">
        <div className="flex items-center gap-1.5 md:gap-2 font-meta text-[11px] md:text-xs text-brand-text-3 mb-3 overflow-x-auto no-scrollbar">
          <span className="hover:text-brand-ink cursor-pointer whitespace-nowrap">Properties</span>
          <ChevronRight className="w-3 h-3 shrink-0" />
          <span className="hover:text-brand-ink cursor-pointer whitespace-nowrap hidden sm:inline">
            Pacific Heights
          </span>
          <ChevronRight className="w-3 h-3 shrink-0 hidden sm:inline" />
          <span className="text-brand-ink whitespace-nowrap">2847 Pacific Avenue</span>
        </div>

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-8">
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-[28px] sm:text-[34px] md:text-[40px] leading-tight font-semibold">
              {p.address_line}
            </h1>
            <div className="font-meta text-xs sm:text-sm text-brand-text-3 mt-1">
              {p.address_city} · {p.beds} bed · {p.baths} bath · {p.sqft.toLocaleString()} sqft · Built{' '}
              {p.year_built} · {p.price_range}
            </div>
          </div>

          <div className="flex md:flex-col items-center md:items-end gap-2 md:gap-2.5 shrink-0">
            <Badge className="bg-brand-info-bg text-brand-info font-meta border-0 text-sm px-3 py-1 rounded-full">
              {p.status}
            </Badge>
            <Button variant="outline" size="sm" className="rounded-pill border-black/10 font-meta">
              <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SECTION NAV — left rail (matches screenshot styling)
   ============================================================ */

function SectionNav({
  active,
  setSection,
  collapsed,
  onToggle,
}: {
  active: Section;
  setSection: (s: Section) => void;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const p = currentProperty;
  type NavItem = { key: Section; icon: any; label: string; count: number | null; alert?: boolean };
  const dealItems: NavItem[] = [
    { key: 'messages', icon: MessageSquare, label: 'Messages', count: p.qa.length },
    { key: 'offers', icon: DollarSign, label: 'Offers', count: p.offers.length },
    { key: 'share', icon: Share2, label: 'Share', count: 3, alert: true },
  ];
  const disclosureItems: NavItem[] = [
    { key: 'overview', icon: BookOpen, label: 'Overview', count: null },
    { key: 'findings', icon: ClipboardCheck, label: 'Findings', count: p.findings.length },
    { key: 'permits', icon: Hammer, label: 'Permits', count: 18 },
    { key: 'documents', icon: FileText, label: 'Documents', count: p.docs_count },
  ];
  const allItems: NavItem[] = [...dealItems, ...disclosureItems];

  if (collapsed) {
    return (
      <aside className="sticky top-20 self-start h-fit">
        {/* Expand button */}
        <div className="flex justify-center mb-3">
          <button
            onClick={onToggle}
            aria-label="Expand sidebar"
            className="w-9 h-9 rounded-pill flex items-center justify-center text-brand-text-3 hover:text-brand-ink hover:bg-white/60 transition-colors"
          >
            <PanelLeftOpen className="w-4 h-4" />
          </button>
        </div>

        <TooltipProvider delayDuration={200}>
          <nav className="flex flex-col gap-1 items-center">
            {dealItems.map(item => (
              <CollapsedNavItem
                key={item.key}
                item={item}
                active={active === item.key}
                onClick={() => setSection(item.key)}
              />
            ))}
            <div className="my-2 w-6 h-px bg-black/10" />
            {disclosureItems.map(item => (
              <CollapsedNavItem
                key={item.key}
                item={item}
                active={active === item.key}
                onClick={() => setSection(item.key)}
              />
            ))}
          </nav>
        </TooltipProvider>
      </aside>
    );
  }

  return (
    <aside className="sticky top-20 self-start h-fit">
      <div className="flex items-center justify-end mb-1 px-3">
        <button
          onClick={onToggle}
          aria-label="Collapse sidebar"
          className="w-6 h-6 rounded flex items-center justify-center text-brand-text-3 hover:text-brand-ink hover:bg-white/60 transition-colors"
        >
          <PanelLeftClose className="w-3.5 h-3.5" />
        </button>
      </div>

      <div data-testid="sidebar-group-deal" className="mb-5">
        <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3 mb-2 px-3">
          Deal
        </div>
        <nav className="flex flex-col gap-1">
          {dealItems.map(item => (
            <ExpandedNavItem
              key={item.key}
              item={item}
              active={active === item.key}
              onClick={() => setSection(item.key)}
            />
          ))}
        </nav>
      </div>

      <div data-testid="sidebar-group-disclosures">
        <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3 mb-2 px-3">
          Disclosures
        </div>
        <nav className="flex flex-col gap-1">
          {disclosureItems.map(item => (
            <ExpandedNavItem
              key={item.key}
              item={item}
              active={active === item.key}
              onClick={() => setSection(item.key)}
            />
          ))}
        </nav>
      </div>

    </aside>
  );
}

type NavItemShape = {
  key: Section;
  icon: any;
  label: string;
  count: number | null;
  alert?: boolean;
};

function ExpandedNavItem({
  item,
  active,
  onClick,
}: {
  item: NavItemShape;
  active: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  return (
    <button
      data-testid={`section-tab-${item.key}`}
      onClick={onClick}
      className={`w-full text-left px-3.5 py-2.5 rounded-pill flex items-center gap-3 transition-colors ${
        active
          ? 'bg-white shadow-sm text-brand-ink font-semibold'
          : 'text-brand-text-2 hover:bg-white/50'
      }`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="text-[15px] flex-1">{item.label}</span>
      {item.count !== null && (
        <span
          className={`font-meta text-xs ${
            item.alert
              ? 'bg-brand-critical text-white rounded-full px-1.5 py-0.5 font-semibold min-w-[18px] text-center'
              : 'text-brand-text-3'
          }`}
        >
          {item.count}
        </span>
      )}
    </button>
  );
}

function CollapsedNavItem({
  item,
  active,
  onClick,
}: {
  item: NavItemShape;
  active: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          aria-label={item.label}
          className={`w-10 h-10 rounded-pill flex items-center justify-center transition-colors relative ${
            active
              ? 'bg-white shadow-sm text-brand-ink'
              : 'text-brand-text-2 hover:bg-white/50'
          }`}
        >
          <Icon className="w-4 h-4" />
          {item.count !== null && item.count > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-white font-meta text-[9px] font-semibold flex items-center justify-center ${
                item.alert ? 'bg-brand-critical' : 'bg-brand-ink'
              }`}
            >
              {item.count}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="font-meta text-xs">
        {item.label}
        {item.count !== null && (
          <span className="ml-1.5 text-brand-text-3">· {item.count}</span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/* ============================================================
   RIGHT RAIL — Ask Closer + source docs + activity
   ============================================================ */

function RightRail({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  if (collapsed) {
    return (
      <aside className="sticky top-20 self-start h-fit">
        <div className="flex justify-center mb-3">
          <button
            onClick={onToggle}
            aria-label="Expand right panel"
            className="w-9 h-9 rounded-pill flex items-center justify-center text-brand-text-3 hover:text-brand-ink hover:bg-white/60 transition-colors"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>

        <TooltipProvider delayDuration={200}>
          <nav className="flex flex-col gap-2 items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onToggle}
                  aria-label="Ask Closer"
                  className="w-10 h-10 rounded-pill bg-brand-ink text-white flex items-center justify-center hover:bg-brand-ink-soft transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="font-meta text-xs">
                Ask Closer
              </TooltipContent>
            </Tooltip>

          </nav>
        </TooltipProvider>
      </aside>
    );
  }

  return (
    <aside className="sticky top-20 self-start h-fit space-y-4">
      <div className="bg-brand-ink text-white rounded-2xl p-5 relative">
        <button
          onClick={onToggle}
          aria-label="Collapse right panel"
          className="absolute top-3 right-3 w-6 h-6 rounded flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <PanelLeftOpen className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4" />
          <span className="font-semibold text-sm">Ask Closer</span>
        </div>
        <p className="font-meta text-xs text-white/70 mb-4 leading-relaxed">
          Questions about this packet? Closer has read all 412 pages.
        </p>
        <Input
          placeholder="What's the biggest risk?"
          className="bg-white/10 border-white/10 text-white placeholder:text-white/50 rounded-pill font-brand text-sm mb-2"
        />
        <div className="flex gap-1.5 flex-wrap">
          {['Summarize', 'Draft counter', 'Compare'].map(x => (
            <button
              key={x}
              className="px-2.5 py-1 rounded-pill bg-white/10 hover:bg-white/15 font-meta text-[11px]"
            >
              {x}
            </button>
          ))}
        </div>
      </div>

    </aside>
  );
}

/* ============================================================
   DOSSIER MODE — main layout with section views
   ============================================================ */

function DossierMode({
  activeSection,
  setSection,
  sidebarCollapsed,
  toggleSidebar,
}: PanelProps) {
  const isDesktop = useIsDesktop();
  return (
    <>
      <HeroBand />
      <MobileSectionPills activeSection={activeSection} setSection={setSection} />

      <div
        className="max-w-[1440px] mx-auto px-4 md:px-6 py-4 md:py-8 grid gap-4 md:gap-6 lg:gap-8 lg:transition-[grid-template-columns] lg:duration-300 grid-cols-1"
        style={
          isDesktop
            ? { gridTemplateColumns: gridCols(sidebarCollapsed) }
            : undefined
        }
      >
        <div className="hidden lg:block">
          <SectionNav
            active={activeSection}
            setSection={setSection}
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
          />
        </div>

        <div className="min-w-0">
          {activeSection === 'overview' && <ViewOverview />}
          {activeSection === 'findings' && <ViewFindings />}
          {activeSection === 'documents' && <ViewDocuments />}
          {activeSection === 'messages' && <ViewMessages />}
          {activeSection === 'offers' && <ViewOffers />}
          {activeSection === 'share' && <ViewShare />}
          {activeSection === 'permits' && <ViewPermits />}
        </div>

      </div>
    </>
  );
}

/* ============================================================
   VIEW — Overview
   ============================================================ */

function ViewOverview() {
  const p = currentProperty;
  const canEdit = useCanEdit();

  // Non-destructive overrides — mirrors PR #196's {original, override} column pair.
  // UI reads override ?? original. Revert = clear the override.
  // TODO(backend): swap these useState hooks for property_summaries.overall_summary_override
  // and repair_estimate_override via the edit-property-summary edge function.
  const [summaryOverride, setSummaryOverride] = useState<string | null>(null);
  const [repairOverride, setRepairOverride] = useState<string | null>(null);

  const summaryEffective = summaryOverride ?? p.summary_lede;
  const repairEffective =
    repairOverride ?? `${formatMoney(p.est_repair_low)}–${formatMoney(p.est_repair_high)}`;

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <h2 className="font-display text-2xl font-semibold">Overview</h2>
        <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0">
          {p.pages_analyzed} pages · {p.docs_count} docs
        </Badge>
      </div>

      {/* Stat row */}
      <div data-testid="overview-stats" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 md:mb-8">
        <StatTile label="Critical" value={p.severity_counts.critical} tone="critical" />
        <StatTile label="Moderate" value={p.severity_counts.moderate} tone="moderate" />
        <StatTile label="Routine" value={p.severity_counts.info} tone="info" />
        <div data-testid="stat-tile-repair-exposure" className="bg-white rounded-xl border border-black/5 p-4 group">
          <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3 mb-1.5">
            Repair exposure
          </div>
          <InlineEdit
            value={repairEffective}
            original={`${formatMoney(p.est_repair_low)}–${formatMoney(p.est_repair_high)}`}
            overridden={repairOverride !== null}
            onSave={next => setRepairOverride(next)}
            onRevert={() => setRepairOverride(null)}
            readOnly={!canEdit}
            variant="body"
            placeholder="Repair range"
            editMeta="Nim · just now"
            valueClassName="font-brand text-2xl font-semibold tabular-nums"
          />
        </div>
      </div>

      {/* Closer's take — hero card */}
      <div className="bg-white rounded-2xl border border-black/5 p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-brand-accent" />
          <span className="font-meta text-[11px] uppercase tracking-wider font-semibold text-brand-accent">
            Closer's take
          </span>
        </div>
        <div className="mb-4">
          <InlineEdit
            value={summaryEffective}
            original={p.summary_lede}
            overridden={summaryOverride !== null}
            onSave={next => setSummaryOverride(next)}
            onRevert={() => setSummaryOverride(null)}
            readOnly={!canEdit}
            multiline
            variant="body"
            placeholder="Describe this property in 2-3 sentences..."
            editMeta="Nim · just now"
            valueClassName="text-[15px] leading-relaxed text-brand-text-2"
          />
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 pt-4 border-t border-black/5 font-meta text-xs">
          <KV k="Pages analyzed" v={p.pages_analyzed.toString()} />
          <KV k="Documents" v={p.docs_count.toString()} />
          <KV k="Year built" v={p.year_built.toString()} />
          <KV k="Lot size" v="4,500 sqft" />
          <KV k="Zoning" v="RH-1" />
          <KV k="Parking" v="1-car garage" />
        </div>
      </div>

      {/* Priority items — pulled from findings, top 2 critical/moderate */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-semibold">Needs attention now</h3>
          <button className="font-meta text-xs text-brand-accent hover:underline">
            All findings →
          </button>
        </div>
        <div className="space-y-2">
          {p.findings.slice(0, 2).map(f => (
            <FindingCard key={f.id} finding={f} compact />
          ))}
        </div>
      </div>

      {/* Suggested asks — chat-style pill row */}
      <div className="bg-gradient-to-br from-brand-sky/20 via-brand-cream/20 to-brand-paper rounded-2xl p-5 border border-black/5">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-brand-ink" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm mb-1">Start a conversation with Closer</div>
            <p className="text-xs text-brand-text-2 leading-relaxed mb-3">
              Not sure where to start? Try one of these.
            </p>
            <div className="flex gap-2 flex-wrap">
              <SuggestedAction>What's the single biggest risk?</SuggestedAction>
              <SuggestedAction>Draft a credit ask for the HVAC</SuggestedAction>
              <SuggestedAction>Summarize for my client</SuggestedAction>
              <SuggestedAction>Compare with 412 Dolores</SuggestedAction>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   VIEW — Findings
   ============================================================ */

function ViewFindings() {
  const p = currentProperty;
  const canEdit = useCanEdit();

  // Default: show all severities except Info (matches the "what's actionable" read)
  const [selectedSeverities, setSelectedSeverities] = useState<Set<Severity>>(
    new Set(['critical', 'high', 'moderate'])
  );
  const [selectedCategory, setSelectedCategory] = useState<Category | 'all'>('all');
  const [selected, setSelected] = useState<string | null>(p.findings[0].id);

  // Non-destructive override state per finding — mirrors PR #196's
  // title_override / description_override / costs_override columns.
  // TODO(backend): swap for the edit-finding edge function; server already stores
  // these as *_override columns and writes diffs to property_history.
  type FindingOverrides = {
    title?: string;
    summary?: string;
    cost?: string;
    archived?: boolean;
  };
  const [overrides, setOverrides] = useState<Record<string, FindingOverrides>>({});
  const patch = (id: string, partial: FindingOverrides) =>
    setOverrides(prev => ({ ...prev, [id]: { ...prev[id], ...partial } }));
  const clearField = (id: string, field: keyof FindingOverrides) =>
    setOverrides(prev => {
      const next = { ...(prev[id] ?? {}) };
      delete next[field];
      return { ...prev, [id]: next };
    });

  // Apply both filters
  const visibleFindings = p.findings.filter(f => {
    const sevMatch = selectedSeverities.has(f.severity);
    const catMatch = selectedCategory === 'all' || f.category === selectedCategory;
    const notArchived = !overrides[f.id]?.archived;
    return sevMatch && catMatch && notArchived;
  });

  // Ensure selection is always valid
  const effectiveSelected =
    visibleFindings.find(f => f.id === selected)?.id ?? visibleFindings[0]?.id ?? null;
  const selectedFinding = p.findings.find(f => f.id === effectiveSelected);

  // Per-severity counts (respect category filter so counts update)
  const baseByCategoryFiltered = p.findings.filter(
    f => selectedCategory === 'all' || f.category === selectedCategory
  );
  const sevCounts: Record<Severity, number> = {
    critical: baseByCategoryFiltered.filter(f => f.severity === 'critical').length,
    high: baseByCategoryFiltered.filter(f => f.severity === 'high').length,
    moderate: baseByCategoryFiltered.filter(f => f.severity === 'moderate').length,
    info: baseByCategoryFiltered.filter(f => f.severity === 'info').length,
  };

  // Per-category counts (respect severity filter)
  const catCounts: Record<Category, number> = FINDING_CATEGORIES.reduce(
    (acc, c) => {
      acc[c.id] = p.findings.filter(
        f => f.category === c.id && selectedSeverities.has(f.severity)
      ).length;
      return acc;
    },
    {} as Record<Category, number>
  );

  const toggleSev = (s: Severity) => {
    setSelectedSeverities(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      // Never allow all four off — revert to "all actionable"
      if (next.size === 0) return new Set(['critical', 'high', 'moderate', 'info']);
      return next;
    });
  };

  const visibleExposure = visibleFindings.reduce(
    (acc, f) => ({ low: acc.low + f.est_low, high: acc.high + f.est_high }),
    { low: 0, high: 0 }
  );

  const pdfDoc = selectedFinding
    ? {
        id: selectedFinding.id,
        filename: selectedFinding.doc ?? 'Disclosure.pdf',
        stored_name: 'sample-disclosure.pdf',
        url: SAMPLE_PDF_URL,
        property_id: p.id ?? 'preview',
      }
    : null;
  const pdfInitialPage = parseCitedPage(selectedFinding?.cited_at);

  return (
    <div className="flex gap-4 lg:gap-6">
      <div className="flex-1 min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-semibold">Findings</h2>
          <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0">
            {visibleFindings.length} of {p.findings.length}
          </Badge>
          <span className="font-meta text-xs text-brand-text-3">
            {formatMoney(visibleExposure.low)}–{formatMoney(visibleExposure.high)} exposure in view
          </span>
        </div>
      </div>

      {/* Severity multi-select (four pills) */}
      <div className="flex items-center gap-1.5 mb-3">
        <span className="font-meta text-[11px] uppercase tracking-wider text-brand-text-3 mr-2">
          Severity
        </span>
        {(['critical', 'high', 'moderate', 'info'] as Severity[]).map(s => {
          const st = sevStyles[s];
          const on = selectedSeverities.has(s);
          return (
            <button
              key={s}
              onClick={() => toggleSev(s)}
              className={`inline-flex items-center gap-2 rounded-pill px-3 py-1.5 font-meta text-xs transition-colors border ${
                on
                  ? `${st.bg} ${st.text} border-transparent font-semibold`
                  : 'bg-white text-brand-text-3 border-black/5 hover:bg-brand-paper'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${on ? st.dot : 'bg-brand-text-4'}`} />
              {st.label}
              <span className={`${on ? '' : 'text-brand-text-3'} font-medium`}>
                {sevCounts[s]}
              </span>
            </button>
          );
        })}
        <button
          onClick={() => setSelectedSeverities(new Set(['critical', 'high', 'moderate', 'info']))}
          className="ml-2 font-meta text-[11px] text-brand-accent hover:underline"
        >
          Select all
        </button>
      </div>

      {/* Category single-select (scroll chip row) */}
      <div className="flex items-center gap-1.5 mb-5 flex-wrap">
        <span className="font-meta text-[11px] uppercase tracking-wider text-brand-text-3 mr-2">
          Category
        </span>
        <CategoryChip
          active={selectedCategory === 'all'}
          onClick={() => setSelectedCategory('all')}
          label="All"
          count={visibleFindings.length}
        />
        {FINDING_CATEGORIES.map(cat => {
          const count = catCounts[cat.id];
          if (count === 0 && selectedCategory !== cat.id) return null;
          const Icon = CATEGORY_ICONS[cat.id];
          return (
            <CategoryChip
              key={cat.id}
              active={selectedCategory === cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              icon={Icon}
              label={cat.label}
              count={count}
            />
          );
        })}
      </div>

      {/* List + detail pane — stacks vertically so right column can host the PDF */}
      <div className="space-y-4">
        <div className="space-y-2">
          {visibleFindings.length === 0 ? (
            <div className="bg-white rounded-2xl border border-black/5 p-8 text-center">
              <p className="font-meta text-sm text-brand-text-3">
                No findings match these filters.
              </p>
              <button
                onClick={() => {
                  setSelectedSeverities(new Set(['critical', 'high', 'moderate', 'info']));
                  setSelectedCategory('all');
                }}
                className="mt-2 font-meta text-xs text-brand-accent hover:underline"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            visibleFindings.map(f => (
              <FindingCard
                key={f.id}
                finding={f}
                selected={f.id === effectiveSelected}
                onClick={() => setSelected(f.id)}
              />
            ))
          )}
        </div>

        {selectedFinding && (() => {
          const o = overrides[selectedFinding.id] ?? {};
          const titleEffective = o.title ?? selectedFinding.title;
          const summaryEffective = o.summary ?? selectedFinding.summary;
          const costOriginal =
            selectedFinding.est_low > 0
              ? `${formatMoney(selectedFinding.est_low)}–${formatMoney(selectedFinding.est_high)}`
              : '';
          const costEffective = o.cost ?? costOriginal;
          return (
          <aside className="bg-white rounded-2xl border border-black/5 overflow-hidden">
            <div className="p-5 border-b border-black/5">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span
                  className={`font-meta text-[10px] uppercase tracking-wider font-semibold ${sevStyles[selectedFinding.severity].text}`}
                >
                  {sevStyles[selectedFinding.severity].label}
                </span>
                {canEdit && (
                  <button
                    onClick={() =>
                      patch(selectedFinding.id, { archived: !o.archived })
                    }
                    className="font-meta text-[11px] text-brand-text-3 hover:text-brand-critical inline-flex items-center gap-1"
                    title={o.archived ? 'Restore to active' : 'Archive this finding'}
                  >
                    {o.archived ? 'Restore' : 'Archive'}
                  </button>
                )}
              </div>
              <InlineEdit
                value={titleEffective}
                original={selectedFinding.title}
                overridden={o.title !== undefined}
                onSave={next => patch(selectedFinding.id, { title: next })}
                onRevert={() => clearField(selectedFinding.id, 'title')}
                readOnly={!canEdit}
                variant="title"
                placeholder="Finding title"
                editMeta="Nim · just now"
                className="mb-1"
              />
              {(selectedFinding.est_low > 0 || o.cost !== undefined) && (
                <div className="mt-3">
                  <InlineEdit
                    value={costEffective}
                    original={costOriginal}
                    overridden={o.cost !== undefined}
                    onSave={next => patch(selectedFinding.id, { cost: next })}
                    onRevert={() => clearField(selectedFinding.id, 'cost')}
                    readOnly={!canEdit}
                    placeholder="e.g. $5k–$8k"
                    editMeta="Nim · just now"
                    valueClassName="font-brand text-[28px] leading-none font-semibold tabular-nums"
                  />
                </div>
              )}
            </div>

            <div className="p-5 border-b border-black/5">
              <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3 mb-2">
                From the report
              </div>
              <InlineEdit
                value={summaryEffective}
                original={selectedFinding.summary}
                overridden={o.summary !== undefined}
                onSave={next => patch(selectedFinding.id, { summary: next })}
                onRevert={() => clearField(selectedFinding.id, 'summary')}
                readOnly={!canEdit}
                multiline
                placeholder="Describe what the inspector found..."
                editMeta="Nim · just now"
                valueClassName="text-sm leading-relaxed text-brand-text-2"
              />
            </div>

            <div className="p-5 border-b border-black/5">
              <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3 mb-2">
                Citation
              </div>
              <button className="flex items-center gap-2 text-sm text-brand-accent hover:underline">
                <FileText className="w-3.5 h-3.5" />
                {selectedFinding.cited_at}
              </button>
              <div className="font-meta text-xs text-brand-text-3 mt-1">{selectedFinding.doc}</div>
            </div>

            {/* B's artifact pattern — Closer suggestion */}
            <div className="p-5 bg-brand-paper-warm">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-brand-accent" />
                <span className="font-meta text-[10px] uppercase tracking-wider text-brand-accent font-semibold">
                  Closer suggests
                </span>
              </div>
              <p className="text-xs text-brand-text-2 mb-3 leading-relaxed">
                <strong className="text-brand-ink">{selectedFinding.action}.</strong>{' '}
                {selectedFinding.severity === 'critical'
                  ? 'This should block contingency removal. Draft specific language to protect your client.'
                  : selectedFinding.severity === 'high'
                  ? 'Material risk. Negotiate aggressively — this warrants either a sizable credit or direct remediation before close.'
                  : selectedFinding.severity === 'moderate'
                  ? 'This is typical ground for a credit ask. Draft a counter with real numbers from the report.'
                  : 'No action needed. Documented for transparency.'}
              </p>
              <div className="flex gap-2 flex-wrap">
                <SuggestedAction small>Draft credit ask</SuggestedAction>
                <SuggestedAction small>Draft contingency</SuggestedAction>
                <SuggestedAction small>Explain to client</SuggestedAction>
              </div>
            </div>
          </aside>
          );
        })()}
      </div>
      </div>

      {/* PDF side panel — mirrors Summary.tsx proportions (w-[43%] sticky) */}
      {pdfDoc && (
        <div className="w-[43%] sticky top-20 h-[calc(100vh-6rem)] hidden lg:block">
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full bg-white rounded-xl border border-black/5">
                <span className="font-meta text-xs text-brand-text-3">Loading PDF…</span>
              </div>
            }
          >
            <PDFSidePanel
              isOpen={true}
              pdf={pdfDoc}
              initialPage={pdfInitialPage}
              onClose={() => { /* always-on in preview */ }}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}

function FindingCard({
  finding,
  selected,
  onClick,
  compact,
}: {
  finding: typeof currentProperty.findings[0];
  selected?: boolean;
  onClick?: () => void;
  compact?: boolean;
}) {
  const s = sevStyles[finding.severity];
  const CatIcon = CATEGORY_ICONS[finding.category];
  const catLabel = FINDING_CATEGORIES.find(c => c.id === finding.category)?.label ?? '';
  return (
    <button
      data-testid={`finding-card-${finding.id}`}
      onClick={onClick}
      className={`w-full text-left bg-white rounded-xl border p-4 transition-colors ${
        selected
          ? 'border-brand-ink/20 shadow-sm ring-1 ring-brand-ink/5'
          : 'border-black/5 hover:border-black/10'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${s.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2.5 mb-1 flex-wrap">
            <span className="font-semibold text-[15px]">{finding.title}</span>
            <span className={`font-meta text-[10px] uppercase tracking-wider font-semibold ${s.text}`}>
              {s.label}
            </span>
            <span className="font-meta text-[10px] text-brand-text-3 inline-flex items-center gap-1 bg-brand-paper px-1.5 py-0.5 rounded">
              <CatIcon className="w-3 h-3" />
              {catLabel}
            </span>
            {finding.est_low > 0 && (
              <span className="ml-auto font-brand font-semibold text-base shrink-0 tabular-nums">
                {formatMoney(finding.est_low)}–{formatMoney(finding.est_high)}
              </span>
            )}
          </div>
          {!compact && (
            <p className="text-sm text-brand-text-2 leading-relaxed mb-2 line-clamp-2">
              {finding.summary}
            </p>
          )}
          <div className="flex items-center gap-4 font-meta text-[11px] text-brand-text-3">
            <span className="inline-flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {finding.cited_at}
            </span>
            <span className="ml-auto">{finding.action}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function CategoryChip({
  active,
  onClick,
  icon: Icon,
  label,
  count,
}: {
  active?: boolean;
  onClick: () => void;
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 font-meta text-xs transition-colors border ${
        active
          ? 'bg-brand-ink text-white border-transparent font-semibold'
          : 'bg-white text-brand-text-2 border-black/5 hover:bg-brand-paper'
      }`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {label}
      <span className={active ? 'text-white/60' : 'text-brand-text-3'}>{count}</span>
    </button>
  );
}

/* ============================================================
   VIEW — Documents
   ============================================================ */

function ViewDocuments() {
  const p = currentProperty;

  type DocStatus = 'complete' | 'analyzing' | 'queued';
  type Doc = {
    name: string;
    type: string;
    pages: number;
    status: DocStatus;
    progress: number; // 0-100
    uploaded: string;
    analyzed_in: string | null;
    finding_ids: string[];
    summary: string | null;
    pages_read?: number; // for analyzing docs
    elapsed?: string; // for analyzing docs
  };

  const docs: Doc[] = [
    {
      name: 'Fire Insurance Declarations.pdf',
      type: 'Insurance',
      pages: 18,
      status: 'analyzing',
      progress: 47,
      uploaded: '2m ago',
      analyzed_in: null,
      finding_ids: [],
      summary: null,
      pages_read: 8,
      elapsed: '0:42',
    },
    {
      name: 'Structural Engineer Report.pdf',
      type: 'Inspection',
      pages: 28,
      status: 'complete',
      progress: 100,
      uploaded: '3d ago',
      analyzed_in: '1m 47s',
      finding_ids: ['f1', 'f11'],
      summary:
        'Licensed structural engineer examined foundation integrity, framing, and load-bearing elements. Primary finding: 7/8" differential settlement at the NE foundation corner with visible diagonal cracking in the basement wall. Recommends pier-and-beam evaluation before contingency removal. All other structural elements conform to expected condition for a 1923 Victorian.',
    },
    {
      name: 'Home Inspection.pdf',
      type: 'Inspection',
      pages: 94,
      status: 'complete',
      progress: 100,
      uploaded: '3d ago',
      analyzed_in: '4m 12s',
      finding_ids: ['f2', 'f3', 'f5', 'f6', 'f7', 'f8', 'f11'],
      summary:
        'Comprehensive home inspection covering systems, structure, and cosmetic condition. 6 material issues flagged: HVAC past expected life, retaining wall lean, missing smoke detectors on upper floor, elevated basement moisture, plus routine items. Roof and electrical panel are recent and well-documented.',
    },
    {
      name: 'NHD Report.pdf',
      type: 'Disclosure',
      pages: 48,
      status: 'complete',
      progress: 100,
      uploaded: '3d ago',
      analyzed_in: '2m 3s',
      finding_ids: ['f4'],
      summary:
        'Natural Hazard Disclosure covering fire, flood, and seismic zones. Property sits in a State Responsibility Area moderate fire zone. Sewer lateral compliance certificate not on file — SF requires upgrade at transfer if non-compliant.',
    },
    {
      name: 'TDS.pdf',
      type: 'Disclosure',
      pages: 22,
      status: 'complete',
      progress: 100,
      uploaded: '3d ago',
      analyzed_in: '1m 8s',
      finding_ids: [],
      summary:
        'Transfer Disclosure Statement from seller. No HOA, no Mello-Roos, no known easements or encroachments. Seller discloses the 2019 electrical panel upgrade and 2021 roof replacement with transferable warranty.',
    },
    {
      name: 'Pest Inspection.pdf',
      type: 'Inspection',
      pages: 12,
      status: 'complete',
      progress: 100,
      uploaded: '3d ago',
      analyzed_in: '0m 38s',
      finding_ids: ['f10'],
      summary:
        'Section 1 clearance issued. No active termite, dryrot, or wood-destroying organism activity at time of inspection.',
    },
    {
      name: 'Preliminary Title Report.pdf',
      type: 'Title',
      pages: 18,
      status: 'complete',
      progress: 100,
      uploaded: '3d ago',
      analyzed_in: '1m 24s',
      finding_ids: ['f9'],
      summary:
        'Title report clean: no outstanding liens, open permits, or encumbrances beyond the standard mortgage. No easements affecting use.',
    },
    {
      name: 'Sewer Lateral Video.pdf',
      type: 'Inspection',
      pages: 4,
      status: 'complete',
      progress: 100,
      uploaded: '3d ago',
      analyzed_in: '0m 28s',
      finding_ids: ['f4'],
      summary:
        'Video inspection of sewer lateral from cleanout to main. Minor root intrusion observed ~18ft. Otherwise functional.',
    },
    {
      name: 'Seismic Retrofit Certificate.pdf',
      type: 'Certificate',
      pages: 6,
      status: 'queued',
      progress: 0,
      uploaded: '1m ago',
      analyzed_in: null,
      finding_ids: [],
      summary: null,
    },
  ];

  const [selected, setSelected] = useState<string>('Structural Engineer Report.pdf');
  const selectedDoc = docs.find(d => d.name === selected)!;

  // Native file picker for "Upload more"
  const fileInputRef = useRef<HTMLInputElement>(null);
  const openFilePicker = () => fileInputRef.current?.click();
  const handleFilesChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    // Demo: log the files. Real wire-up: POST to /functions/v1/upload-documents.
    // TODO(backend): kick off classification pipeline + return doc rows to append here.
    console.log(
      `[preview] User selected ${files.length} file${files.length === 1 ? '' : 's'}:`,
      files.map(f => ({ name: f.name, size: f.size, type: f.type }))
    );
    // Reset so selecting the same file twice still fires the event
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Collapse state for in-progress analyzing doc banners
  const [collapsedAnalyzing, setCollapsedAnalyzing] = useState<Record<string, boolean>>({});
  const toggleAnalyzingCollapse = (name: string) =>
    setCollapsedAnalyzing(prev => ({ ...prev, [name]: !prev[name] }));
  const selectedFindings = p.findings.filter(f => selectedDoc.finding_ids.includes(f.id));

  const analyzingDocs = docs.filter(d => d.status === 'analyzing');
  const queuedDocs = docs.filter(d => d.status === 'queued');
  const completeDocs = docs.filter(d => d.status === 'complete');

  const statusStyle: Record<DocStatus, { bg: string; text: string; dot: string; label: string }> = {
    complete: { bg: 'bg-brand-info-bg', text: 'text-brand-info', dot: 'bg-brand-info', label: 'Complete' },
    analyzing: { bg: 'bg-brand-sky-soft', text: 'text-brand-ink', dot: 'bg-brand-sky', label: 'Analyzing' },
    queued: { bg: 'bg-brand-paper', text: 'text-brand-text-3', dot: 'bg-brand-text-4', label: 'Queued' },
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-semibold">Documents</h2>
          <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0">
            {docs.length} files
          </Badge>
          <span className="font-meta text-xs text-brand-text-3">
            {completeDocs.reduce((s, d) => s + d.pages, 0)} pages analyzed
            {analyzingDocs.length > 0 && ` · ${analyzingDocs.length} in progress`}
            {queuedDocs.length > 0 && ` · ${queuedDocs.length} queued`}
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.heic,.webp,.tiff,.eml,.msg"
          onChange={handleFilesChosen}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />
        <Button
          size="sm"
          onClick={openFilePicker}
          className="rounded-pill bg-brand-ink hover:bg-brand-ink-soft text-white font-meta"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Upload more
        </Button>
      </div>

      {/* Active analysis banner — shows only when a doc is analyzing */}
      {analyzingDocs.map(d => {
        const isCollapsed = collapsedAnalyzing[d.name];
        return (
          <div
            key={d.name}
            className="mb-4 bg-gradient-to-r from-brand-sky/25 via-brand-sky-soft/40 to-brand-cream/30 border border-black/5 rounded-2xl p-5"
          >
            <div
              className={`flex items-center justify-between ${isCollapsed ? '' : 'mb-3'}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0">
                  <Loader2 className="w-4 h-4 text-brand-ink animate-spin" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-sm">{d.name}</span>
                    <Badge className={`${statusStyle.analyzing.bg} ${statusStyle.analyzing.text} font-meta border-0 text-[10px]`}>
                      Analyzing
                    </Badge>
                    {isCollapsed && (
                      <span className="font-meta text-[11px] text-brand-text-3 tabular-nums">
                        · {d.progress}%
                      </span>
                    )}
                  </div>
                  {!isCollapsed && (
                    <div className="font-meta text-xs text-brand-text-3">
                      <Sparkles className="w-3 h-3 inline -mt-0.5 mr-1 text-brand-accent" />
                      Closer is reading page {d.pages_read} of {d.pages} · {d.elapsed} elapsed
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!isCollapsed && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-pill font-meta text-xs text-brand-text-3"
                  >
                    Cancel
                  </Button>
                )}
                <button
                  onClick={() => toggleAnalyzingCollapse(d.name)}
                  aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-brand-text-3 hover:text-brand-ink hover:bg-white/60 transition-colors"
                >
                  {isCollapsed ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronUp className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {!isCollapsed && (
              <>
                {/* Progress bar */}
                <div className="relative h-1.5 bg-white/60 rounded-full overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-brand-ink rounded-full transition-all duration-500"
                    style={{ width: `${d.progress}%` }}
                  />
                  {/* Pulse overlay */}
                  <div
                    className="absolute inset-y-0 w-16 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                    style={{
                      left: `${d.progress}%`,
                      animation: 'pulse-shimmer 2s ease-in-out infinite',
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 font-meta text-[11px] text-brand-text-3">
                  <span>{d.progress}% complete</span>
                  <span>~{Math.round((100 - d.progress) / 30)}m remaining</span>
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* Filter row */}
      <div className="flex gap-1 font-meta text-xs mb-4">
        <FilterChip active>All · {docs.length}</FilterChip>
        <FilterChip>Inspection · {docs.filter(d => d.type === 'Inspection').length}</FilterChip>
        <FilterChip>Disclosure · {docs.filter(d => d.type === 'Disclosure').length}</FilterChip>
        <FilterChip>Title · {docs.filter(d => d.type === 'Title').length}</FilterChip>
        <FilterChip>Other · {docs.filter(d => !['Inspection', 'Disclosure', 'Title'].includes(d.type)).length}</FilterChip>
      </div>

      {/* Main: doc list + rich doc detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        {/* Doc list (compact) */}
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden self-start">
          {docs.map(d => {
            const isActive = d.name === selected;
            const st = statusStyle[d.status];
            return (
              <button
                key={d.name}
                onClick={() => setSelected(d.name)}
                className={`w-full text-left px-4 py-3 border-b border-black/5 last:border-0 transition-colors ${
                  isActive ? 'bg-brand-paper-warm' : 'hover:bg-brand-paper-warm/40'
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <FileText className="w-4 h-4 text-brand-text-3 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate mb-1">{d.name}</div>
                    <div className="flex items-center gap-2 font-meta text-[11px] text-brand-text-3">
                      <span>{d.type}</span>
                      <span>·</span>
                      <span>{d.pages || '—'} pg</span>
                      {d.status === 'complete' && d.finding_ids.length > 0 && (
                        <>
                          <span>·</span>
                          <span className="text-brand-ink font-medium">{d.finding_ids.length} flag{d.finding_ids.length > 1 ? 's' : ''}</span>
                        </>
                      )}
                    </div>
                    {d.status === 'analyzing' && (
                      <div className="mt-2">
                        <div className="relative h-1 bg-black/5 rounded-full overflow-hidden">
                          <div
                            className="absolute inset-y-0 left-0 bg-brand-ink rounded-full transition-all"
                            style={{ width: `${d.progress}%` }}
                          />
                        </div>
                        <div className="font-meta text-[10px] text-brand-text-3 mt-1">
                          {d.progress}% · {d.elapsed}
                        </div>
                      </div>
                    )}
                    {d.status === 'queued' && (
                      <div className="mt-1.5 inline-flex items-center gap-1 font-meta text-[10px] text-brand-text-3">
                        <Clock className="w-2.5 h-2.5" /> Waiting to analyze
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Selected doc detail — rich panel */}
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-black/5">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 font-meta text-[11px] text-brand-text-3 mb-2">
                  <FileText className="w-3 h-3" />
                  <span>{selectedDoc.type}</span>
                  <span>·</span>
                  <span>{selectedDoc.pages || '—'} pages</span>
                  <span>·</span>
                  <span>Uploaded {selectedDoc.uploaded}</span>
                </div>
                <h3 className="font-display text-xl font-semibold leading-tight">{selectedDoc.name}</h3>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" className="rounded-pill border-black/10 font-meta text-xs">
                  <ExternalLink className="w-3 h-3 mr-1.5" /> Open PDF
                </Button>
                <Button size="sm" variant="outline" className="rounded-pill border-black/10 font-meta text-xs">
                  <Download className="w-3 h-3 mr-1.5" />
                </Button>
              </div>
            </div>

            {/* Status ribbon */}
            {selectedDoc.status === 'complete' && (
              <div className="flex items-center gap-3 pt-3 border-t border-black/5 font-meta text-xs">
                <span className="inline-flex items-center gap-1.5 text-brand-info">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Analyzed in {selectedDoc.analyzed_in}
                </span>
                <span className="text-brand-text-3">·</span>
                <span className="text-brand-text-3">
                  {selectedDoc.finding_ids.length} finding
                  {selectedDoc.finding_ids.length === 1 ? '' : 's'} surfaced
                </span>
                {selectedFindings.some(f => f.severity === 'critical') && (
                  <>
                    <span className="text-brand-text-3">·</span>
                    <span className="inline-flex items-center gap-1 text-brand-critical">
                      <AlertCircle className="w-3.5 h-3.5" /> Contains critical finding
                    </span>
                  </>
                )}
              </div>
            )}
            {selectedDoc.status === 'analyzing' && (
              <div className="flex items-center gap-2 pt-3 border-t border-black/5 font-meta text-xs text-brand-text-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Closer is reading page {selectedDoc.pages_read} of {selectedDoc.pages} · {selectedDoc.elapsed} elapsed
              </div>
            )}
            {selectedDoc.status === 'queued' && (
              <div className="flex items-center gap-2 pt-3 border-t border-black/5 font-meta text-xs text-brand-text-3">
                <Clock className="w-3.5 h-3.5" />
                Queued for analysis after current document completes
              </div>
            )}
          </div>

          {/* Body — summary + findings */}
          {selectedDoc.status === 'complete' && (
            <>
              {/* Closer summary */}
              <section className="p-6 border-b border-black/5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-brand-accent" />
                  <span className="font-meta text-[11px] uppercase tracking-wider font-semibold text-brand-accent">
                    Closer's summary
                  </span>
                </div>
                <p className="text-[15px] leading-relaxed text-brand-text-2 mb-3">
                  {selectedDoc.summary}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <SuggestedAction small>Rewrite for my client</SuggestedAction>
                  <SuggestedAction small>What should I ask next?</SuggestedAction>
                  <SuggestedAction small>Compare with another doc</SuggestedAction>
                </div>
              </section>

              {/* Per-doc findings */}
              <section className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ClipboardCheck className="w-4 h-4 text-brand-text-3" />
                  <h4 className="font-display text-lg font-semibold">
                    Findings from this document
                  </h4>
                  <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0">
                    {selectedFindings.length}
                  </Badge>
                </div>

                {selectedFindings.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-6 h-6 text-brand-info mx-auto mb-2" />
                    <p className="text-sm text-brand-text-2">
                      No issues flagged in this document.
                    </p>
                    <p className="font-meta text-xs text-brand-text-3 mt-1">
                      Routine document, no action needed.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedFindings.map(f => (
                      <FindingCard key={f.id} finding={f} />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}

          {/* Analyzing state body — big progress readout */}
          {selectedDoc.status === 'analyzing' && (
            <section className="p-8">
              <div className="max-w-md mx-auto text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-sky to-brand-cream flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-6 h-6 text-brand-ink animate-spin" />
                </div>
                <h4 className="font-display text-lg font-semibold mb-1">Closer is reading</h4>
                <p className="text-sm text-brand-text-2 mb-6">
                  Pulling structure, findings, and citations from page {selectedDoc.pages_read} of{' '}
                  {selectedDoc.pages}.
                </p>

                <div className="relative h-2 bg-black/5 rounded-full overflow-hidden mb-2">
                  <div
                    className="absolute inset-y-0 left-0 bg-brand-ink rounded-full transition-all"
                    style={{ width: `${selectedDoc.progress}%` }}
                  />
                </div>
                <div className="flex justify-between font-meta text-xs text-brand-text-3">
                  <span>{selectedDoc.progress}% complete</span>
                  <span>
                    {selectedDoc.pages_read} / {selectedDoc.pages} pages · {selectedDoc.elapsed}
                  </span>
                </div>
              </div>

              {/* Live log — what Closer is doing right now */}
              <div className="mt-8 bg-brand-paper-warm rounded-xl border border-black/5 p-4 max-w-md mx-auto">
                <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3 mb-2">
                  Live
                </div>
                <div className="space-y-1.5 font-mono text-[11px] text-brand-text-2">
                  <LogLine>Indexed policy declarations page</LogLine>
                  <LogLine>Extracting coverage limits...</LogLine>
                  <LogLine active>Reading exclusions (pg 8)</LogLine>
                </div>
              </div>
            </section>
          )}

          {selectedDoc.status === 'queued' && (
            <section className="p-10 text-center">
              <Clock className="w-10 h-10 text-brand-text-3 mx-auto mb-3" />
              <h4 className="font-display text-lg font-semibold mb-1">Queued for analysis</h4>
              <p className="text-sm text-brand-text-2 max-w-sm mx-auto">
                Closer will start this document as soon as {analyzingDocs[0]?.name ?? 'the current analysis'} completes.
              </p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function LogLine({ children, active }: { children: React.ReactNode; active?: boolean }) {
  return (
    <div className={`flex items-center gap-2 ${active ? 'text-brand-ink' : 'text-brand-text-3'}`}>
      {active ? (
        <Loader2 className="w-2.5 h-2.5 animate-spin shrink-0" />
      ) : (
        <CheckCircle2 className="w-2.5 h-2.5 text-brand-info shrink-0" />
      )}
      <span className="font-mono">{children}</span>
    </div>
  );
}

/* ============================================================
   VIEW — Messages — inbound questions from agents/clients/inspectors
   ============================================================ */

type MessageStatus = 'unanswered' | 'answered' | 'resolved';
type MessageFrom = 'buying_agent' | 'client' | 'co_agent';

type Message = {
  id: string;
  from_name: string;
  from_company: string;
  from_role: MessageFrom;
  status: MessageStatus;
  ts: string;
  question: string;
  reply?: {
    body: string;
    author: string;
    ts: string;
  };
  cite?: { doc: string; page: number; quote: string };
  closer_draft?: string;
};

const MESSAGES: Message[] = [
  {
    id: 'm1',
    from_name: 'Kim Chen',
    from_company: 'Compass',
    from_role: 'buying_agent',
    status: 'unanswered',
    ts: '12m ago',
    question:
      "My client is asking whether the foundation settlement noted on page 87 is actively progressing, or if it's been stable. Any recent monitoring reports or movement readings you can share?",
    closer_draft:
      "The structural engineer's report (pg 87) notes the 7/8\" differential was measured at the time of inspection and recommends pier-and-beam evaluation before contingency removal. There's no subsequent monitoring data on file — I'd offer to split the cost of an engineer visit to re-measure before contingency deadline.",
  },
  {
    id: 'm2',
    from_name: 'Sarah Chen',
    from_company: "Sotheby's",
    from_role: 'buying_agent',
    status: 'answered',
    ts: '2h ago',
    question: 'Is this property in a fire hazard zone?',
    reply: {
      body:
        "Yes — the NHD places the property in a State Responsibility Area moderate fire hazard zone. Full context is on page 42 of the NHD report. Insurance is current (State Farm, no fire exclusions).",
      author: 'You',
      ts: '1h ago',
    },
    cite: {
      doc: 'NHD Report.pdf',
      page: 42,
      quote:
        'Property located in a State Responsibility Area. Fire hazard severity zone: MODERATE.',
    },
  },
  {
    id: 'm3',
    from_name: 'Jasmine Park',
    from_company: 'Buyer — direct',
    from_role: 'client',
    status: 'unanswered',
    ts: '4h ago',
    question:
      "I noticed the HVAC is listed as 19 years old. Will it be replaced before close, or is the seller offering a credit?",
    closer_draft:
      "The seller has not committed to a position yet. Typical market practice for a 19-year HVAC in SF is either a $5k credit at close or a pre-close replacement (~$4.5k–$7k for a like-for-like swap). I'd recommend drafting a credit ask before the contingency deadline.",
  },
  {
    id: 'm4',
    from_name: 'Luis Rodriguez',
    from_company: 'Keller Williams',
    from_role: 'co_agent',
    status: 'answered',
    ts: '1d ago',
    question: 'Is there any HOA or Mello-Roos on this property?',
    reply: {
      body:
        "No HOA — single-family home. No Mello-Roos disclosed. Confirmed in the TDS section 2.1 and NHD page 31.",
      author: 'You',
      ts: '22h ago',
    },
  },
  {
    id: 'm5',
    from_name: 'Michael Ng',
    from_company: 'Wells Fargo Home Mortgage',
    from_role: 'client',
    status: 'resolved',
    ts: '2d ago',
    question: 'Can you confirm the lot size and zoning for underwriting?',
    reply: {
      body:
        "Lot is 4,500 sqft. Zoning is RH-1 (single-family residential). Confirmed on preliminary title report, pg 4.",
      author: 'You',
      ts: '2d ago',
    },
  },
  {
    id: 'm6',
    from_name: 'Alex Thompson',
    from_company: 'Independent inspector',
    from_role: 'client',
    status: 'answered',
    ts: '3d ago',
    question:
      'Has the retaining wall on the south boundary been inspected by a licensed engineer, or only flagged by the general inspector?',
    reply: {
      body:
        "Only flagged by the general inspector so far. No engineer-stamped report on the retaining wall. I can schedule one if you'd like.",
      author: 'You',
      ts: '3d ago',
    },
  },
];

type MessageFilter = 'all' | 'unanswered' | 'clients' | 'agents';

function ViewMessages() {
  const [filter, setFilter] = useState<MessageFilter>('all');
  const [selected, setSelected] = useState<string>(MESSAGES[0].id);

  const unansweredCount = MESSAGES.filter(m => m.status === 'unanswered').length;
  const resolvedCount = MESSAGES.filter(m => m.status === 'resolved').length;

  const visible = MESSAGES.filter(m => {
    if (filter === 'unanswered') return m.status === 'unanswered';
    if (filter === 'clients') return m.from_role === 'client';
    if (filter === 'agents')
      return m.from_role === 'buying_agent' || m.from_role === 'co_agent';
    return true;
  });

  const selectedMsg = visible.find(m => m.id === selected) ?? visible[0];

  const roleStyles: Record<MessageFrom, { label: string; bg: string; text: string }> = {
    buying_agent: { label: "Buyer's agent", bg: 'bg-brand-info-bg', text: 'text-brand-info' },
    client: { label: 'Client', bg: 'bg-brand-sky-soft', text: 'text-brand-ink' },
    co_agent: { label: 'Co-agent', bg: 'bg-brand-accent/15', text: 'text-brand-accent' },
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-semibold">Messages</h2>
          <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0">
            {MESSAGES.length} total
          </Badge>
          {unansweredCount > 0 && (
            <span className="font-meta text-xs text-brand-critical font-medium">
              {unansweredCount} unanswered
            </span>
          )}
        </div>
        <div className="flex gap-1 font-meta text-xs">
          <FilterChip active={filter === 'all'}>
            <button onClick={() => setFilter('all')} className="contents">All · {MESSAGES.length}</button>
          </FilterChip>
          <FilterChip active={filter === 'unanswered'}>
            <button onClick={() => setFilter('unanswered')} className="contents">
              Unanswered · {unansweredCount}
            </button>
          </FilterChip>
          <FilterChip active={filter === 'agents'}>
            <button onClick={() => setFilter('agents')} className="contents">From agents</button>
          </FilterChip>
          <FilterChip active={filter === 'clients'}>
            <button onClick={() => setFilter('clients')} className="contents">From clients</button>
          </FilterChip>
        </div>
      </div>

      {/* List + thread detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* Message list */}
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden self-start">
          {visible.map((m, i) => {
            const isActive = m.id === selectedMsg?.id;
            const rs = roleStyles[m.from_role];
            return (
              <button
                key={m.id}
                data-testid={`message-item-${m.id}`}
                onClick={() => setSelected(m.id)}
                className={`w-full text-left px-4 py-3.5 border-b border-black/5 last:border-0 transition-colors ${
                  isActive ? 'bg-brand-paper-warm' : 'hover:bg-brand-paper-warm/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-brand-paper flex items-center justify-center shrink-0 font-meta text-xs font-semibold text-brand-text-2">
                    {m.from_name
                      .split(' ')
                      .map(n => n[0])
                      .join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 mb-0.5">
                      <span className="text-sm font-semibold truncate">{m.from_name}</span>
                      <span className="font-meta text-[11px] text-brand-text-3 shrink-0">{m.ts}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Badge
                        className={`${rs.bg} ${rs.text} font-meta border-0 text-[10px]`}
                      >
                        {rs.label}
                      </Badge>
                      {m.status === 'unanswered' && (
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-critical" />
                      )}
                      {m.status === 'resolved' && (
                        <CheckCircle2 className="w-3 h-3 text-brand-info" />
                      )}
                    </div>
                    <p className="text-xs text-brand-text-2 leading-snug line-clamp-2">
                      {m.question}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Thread detail */}
        {selectedMsg && (
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
            {/* Thread header */}
            <div className="px-6 py-4 border-b border-black/5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-paper flex items-center justify-center shrink-0 font-meta text-xs font-semibold text-brand-text-2">
                {selectedMsg.from_name
                  .split(' ')
                  .map(n => n[0])
                  .join('')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-sm">{selectedMsg.from_name}</span>
                  <span className="font-meta text-[11px] text-brand-text-3">
                    {selectedMsg.from_company}
                  </span>
                  <Badge
                    className={`${roleStyles[selectedMsg.from_role].bg} ${
                      roleStyles[selectedMsg.from_role].text
                    } font-meta border-0 text-[10px]`}
                  >
                    {roleStyles[selectedMsg.from_role].label}
                  </Badge>
                </div>
                <div className="font-meta text-[11px] text-brand-text-3">
                  {selectedMsg.ts} ·{' '}
                  {selectedMsg.status === 'unanswered'
                    ? 'Awaiting response'
                    : selectedMsg.status === 'answered'
                      ? 'Replied'
                      : 'Resolved'}
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="rounded-pill font-meta text-xs">
                  Mark resolved
                </Button>
                <Button variant="ghost" size="icon" className="w-8 h-8 rounded-full">
                  <MoreHorizontal className="w-4 h-4 text-brand-text-3" />
                </Button>
              </div>
            </div>

            {/* Incoming question */}
            <div className="px-6 py-5 border-b border-black/5">
              <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3 mb-2">
                Question
              </div>
              <p className="text-[15px] leading-relaxed text-brand-ink">
                {selectedMsg.question}
              </p>
            </div>

            {/* Your reply, if any */}
            {selectedMsg.reply && (
              <div className="px-6 py-5 border-b border-black/5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-full bg-brand-ink text-white text-[10px] font-meta font-semibold flex items-center justify-center">
                    N
                  </div>
                  <span className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3">
                    {selectedMsg.reply.author} replied · {selectedMsg.reply.ts}
                  </span>
                </div>
                <p className="text-[14px] leading-relaxed text-brand-text-2 mb-3">
                  {selectedMsg.reply.body}
                </p>
                {selectedMsg.cite && (
                  <div className="bg-brand-paper-warm border border-black/5 rounded-xl p-3 max-w-md">
                    <div className="flex items-center gap-2 text-xs text-brand-accent mb-1">
                      <FileText className="w-3.5 h-3.5" />
                      <span className="font-meta">
                        Cited from {selectedMsg.cite.doc}
                      </span>
                    </div>
                    <div className="font-mono text-[11px] text-brand-text-2 leading-relaxed">
                      "{selectedMsg.cite.quote}"
                    </div>
                    <button className="font-meta text-[11px] text-brand-accent hover:underline mt-2">
                      Open page {selectedMsg.cite.page} →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Closer's draft — only for unanswered */}
            {selectedMsg.status === 'unanswered' && selectedMsg.closer_draft && (
              <div className="px-6 py-5 bg-gradient-to-br from-brand-sky/12 via-brand-cream/12 to-brand-paper-warm/50 border-b border-black/5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-sky to-brand-cream flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-brand-ink" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-meta text-[10px] uppercase tracking-wider font-semibold text-brand-accent">
                        Closer's draft
                      </span>
                      <span className="font-meta text-[10px] text-brand-text-3">
                        Grounded in the packet — review before sending
                      </span>
                    </div>
                    <p className="text-[14px] leading-relaxed text-brand-text-2 mb-3">
                      {selectedMsg.closer_draft}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="rounded-pill bg-brand-ink hover:bg-brand-ink-soft text-white font-meta text-xs"
                      >
                        Use this draft
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-pill border-black/10 font-meta text-xs"
                      >
                        Regenerate
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="rounded-pill font-meta text-xs text-brand-text-3"
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Reply composer */}
            <div className="px-6 py-4 bg-brand-paper-warm/50">
              <div className="bg-white rounded-2xl border border-black/10 shadow-sm p-2 pl-4 flex items-end gap-2">
                <button className="w-8 h-8 rounded-full hover:bg-brand-paper flex items-center justify-center text-brand-text-3">
                  <Paperclip className="w-4 h-4" />
                </button>
                <Input
                  placeholder={
                    selectedMsg.status === 'unanswered'
                      ? 'Reply to ' + selectedMsg.from_name.split(' ')[0] + '...'
                      : 'Send a follow-up...'
                  }
                  className="flex-1 border-0 shadow-none focus-visible:ring-0 font-brand text-sm bg-transparent"
                />
                <button className="w-8 h-8 rounded-full bg-brand-ink hover:bg-brand-ink-soft text-white flex items-center justify-center">
                  <ArrowUp className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-2 mt-2.5 font-meta text-[11px]">
                <SuggestedAction small>Draft with Closer</SuggestedAction>
                <SuggestedAction small>Insert citation</SuggestedAction>
                <SuggestedAction small>Forward to seller</SuggestedAction>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 font-meta text-[11px] text-brand-text-3 text-center">
        Messages come from the people you've shared this property with. {resolvedCount} resolved.
      </div>
    </div>
  );
}

/* ============================================================
   VIEW — Offers
   ============================================================ */

function ViewOffers() {
  const p = currentProperty;
  const totalHigh = Math.max(...p.offers.map(o => o.amount));

  // Role-aware: "Log offer" only shows for roles that submit/track outgoing offers
  // (buyer's agent, buyer, co-listing agent). Hidden for listing_agent, seller,
  // and TC since they receive offers through the system rather than log them.
  // TODO(backend): replace the localStorage demo with the property's role column
  // (or the logged-in user's listing_role on the property_access join).
  const role =
    (typeof window !== 'undefined' &&
      window.localStorage.getItem('discloser.demo.role')) ||
    'listing_agent';
  const canLogOffer = !['listing_agent', 'listing_tc', 'seller'].includes(role);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-semibold">Offers</h2>
          <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0">
            {p.offers.length} received
          </Badge>
          <span className="font-meta text-xs text-brand-text-3">
            High {formatMoney(totalHigh)} · Ask {p.price_range}
          </span>
        </div>
        {canLogOffer && (
          <Button size="sm" variant="outline" className="rounded-pill border-black/10 font-meta">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Log offer
          </Button>
        )}
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5 md:mb-6">
        <StatTile label="Highest" value={formatMoney(totalHigh)} />
        <StatTile label="Median" value={formatMoney(4350000)} />
        <StatTile label="Lowest" value={formatMoney(Math.min(...p.offers.map(o => o.amount)))} />
        <StatTile label="Vs. list" value="+1.2%" tone="info" />
      </div>

      {/* Offers list */}
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden mb-6 overflow-x-auto">
        {(() => {
          const cols = 'grid grid-cols-[1.4fr_110px_90px_1.2fr_130px] gap-4 md:gap-6 min-w-[640px]';
          return (
            <>
              <div
                className={`${cols} px-5 py-3 border-b border-black/5 font-meta text-[10px] uppercase tracking-wider text-brand-text-3`}
              >
                <div>From</div>
                <div className="text-right">Amount</div>
                <div className="text-right">vs. list</div>
                <div>Terms</div>
                <div className="text-right">Status</div>
              </div>
              {p.offers.map((o, i) => {
                const listMid = 4400000;
                const deltaPct = ((o.amount - listMid) / listMid) * 100;
                return (
                  <div
                    key={i}
                    className={`${cols} px-5 py-4 items-center ${
                      i !== p.offers.length - 1 ? 'border-b border-black/5' : ''
                    } hover:bg-brand-paper-warm/40 cursor-pointer`}
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{o.from}</div>
                      <div className="font-meta text-[11px] text-brand-text-3">{o.received}</div>
                    </div>
                    <div className="font-brand text-lg font-semibold text-right tabular-nums">
                      {formatMoney(o.amount)}
                    </div>
                    <div
                      className={`font-meta text-xs font-medium flex items-center gap-1 justify-end tabular-nums ${
                        deltaPct >= 0 ? 'text-brand-info' : 'text-brand-critical'
                      }`}
                    >
                      {deltaPct >= 0 ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : (
                        <TrendingDown className="w-3 h-3" />
                      )}
                      {deltaPct >= 0 ? '+' : ''}
                      {deltaPct.toFixed(1)}%
                    </div>
                    <div className="font-meta text-xs text-brand-text-2 truncate">{o.terms}</div>
                    <div className="flex justify-end">
                      <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0 whitespace-nowrap">
                        {o.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </>
          );
        })()}
      </div>

      {/* Closer's recommendation — B-style artifact */}
      <div data-testid="closer-recommendation" className="bg-gradient-to-br from-brand-sky/15 via-brand-cream/15 to-brand-paper rounded-2xl p-5 border border-black/5">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-brand-ink" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm mb-1">Closer's read on these offers</div>
            <p className="text-sm text-brand-text-2 leading-relaxed mb-3">
              Chen's all-cash 14-day close at $4.425M is your cleanest. Rodriguez countered below list
              and wants 30 days — slow and likely to fall through on the foundation finding. Kim is in
              range but inspection-heavy. I'd counter Chen with $4.475M and accept; backup to Kim.
            </p>
            <div className="flex gap-2 flex-wrap">
              <SuggestedAction>Draft counter to Chen</SuggestedAction>
              <SuggestedAction>Rank by net terms</SuggestedAction>
              <SuggestedAction>Notify backup offer</SuggestedAction>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   VIEW — Share: dashboard for recipients, pending pushes, inbound requests
   ============================================================ */

type InviteRole = 'co_listing_agent' | 'buying_agent' | 'buying_tc' | 'listing_tc' | 'buyer' | 'seller';

const INVITE_ROLE_OPTIONS: {
  value: InviteRole;
  label: string;
  sub: string;
  note: string;
  dot: string;
}[] = [
  {
    value: 'buying_agent',
    label: "Buyer's agent",
    sub: "Representing the buyer's side",
    note: 'Sees findings, can ask questions, can submit offers',
    dot: 'bg-brand-info',
  },
  {
    value: 'buyer',
    label: 'Buyer',
    sub: 'Buying the property directly',
    note: 'Plain-language summary, no internal notes or cost exposure',
    dot: 'bg-brand-info',
  },
  {
    value: 'co_listing_agent',
    label: 'Co-listing agent',
    sub: 'Share full listing access',
    note: 'Full access — findings, offers, Q&A, and the ability to share further',
    dot: 'bg-brand-moderate',
  },
  {
    value: 'seller',
    label: 'Seller',
    sub: 'Confirm before sharing with clients',
    note: 'Plain-language summary; approve what the buyer side will see',
    dot: 'bg-brand-accent',
  },
  {
    value: 'listing_tc',
    label: 'Transaction coordinator',
    sub: 'Manage paperwork on your behalf',
    note: 'Read-only across the packet — checks completeness and signatures',
    dot: 'bg-brand-sky',
  },
];

type InviteRoleValue = InviteRole;

const INVITE_ROLE_LABELS: Record<InviteRoleValue, string> = {
  buying_agent: "Buyer's agent",
  buyer: 'Buyer',
  buying_tc: 'Buying TC',
  co_listing_agent: 'Co-listing agent',
  seller: 'Seller',
  listing_tc: 'Listing TC',
};

type VisibilityKey = 'findings' | 'summaries' | 'documents' | 'offers' | 'participants';

function defaultVisibilityFor(role: InviteRoleValue): Record<VisibilityKey, boolean> {
  switch (role) {
    case 'buyer':
      return { findings: false, summaries: true, documents: false, offers: true, participants: true };
    case 'seller':
      return { findings: false, summaries: true, documents: false, offers: false, participants: false };
    case 'co_listing_agent':
    case 'listing_tc':
      return { findings: true, summaries: true, documents: true, offers: true, participants: true };
    case 'buying_agent':
    default:
      return { findings: true, summaries: true, documents: true, offers: true, participants: true };
  }
}

const LISTING_AGENT_ROLES = new Set(['listing_agent', 'co_listing_agent']);

function ViewShare() {
  type Recipient = {
    id: string;
    name: string;
    company: string;
    email: string;
    role: 'Buyer agent' | 'Buyer' | 'Lender' | 'Inspector';
    shared_at: string;
    last_viewed: string | null;
    docs_opened: number;
    docs_total: number;
    has_all_docs: boolean;
    missing_docs?: string[];
  };

  type InboundRequest = {
    id: string;
    from_name: string;
    from_company: string;
    type: 'sign' | 'fill' | 'upload' | 'respond';
    doc_name: string;
    description: string;
    requested_at: string;
    due: string;
    urgency: 'high' | 'medium' | 'low';
  };

  const recipients: Recipient[] = [
    {
      id: 'r1',
      name: 'Kim Chen',
      company: 'Compass',
      email: 'kim@compass.com',
      role: 'Buyer agent',
      shared_at: '3d ago',
      last_viewed: '2h ago',
      docs_opened: 18,
      docs_total: 23,
      has_all_docs: false,
      missing_docs: ['Sewer Lateral Video.pdf', 'Fire Insurance Declarations.pdf'],
    },
    {
      id: 'r2',
      name: 'Luis Rodriguez',
      company: 'Keller Williams',
      email: 'luis.rodriguez@kw.com',
      role: 'Buyer agent',
      shared_at: '3d ago',
      last_viewed: '1d ago',
      docs_opened: 12,
      docs_total: 23,
      has_all_docs: false,
      missing_docs: ['Fire Insurance Declarations.pdf'],
    },
    {
      id: 'r3',
      name: 'Sarah Chen',
      company: "Sotheby's",
      email: 'schen@sothebys.com',
      role: 'Buyer agent',
      shared_at: '1d ago',
      last_viewed: '6h ago',
      docs_opened: 21,
      docs_total: 21,
      has_all_docs: true,
    },
    {
      id: 'r4',
      name: 'Michael Ng',
      company: 'Wells Fargo Home Mortgage',
      email: 'mng@wellsfargo.com',
      role: 'Lender',
      shared_at: '2d ago',
      last_viewed: null,
      docs_opened: 0,
      docs_total: 23,
      has_all_docs: true,
    },
    {
      id: 'r5',
      name: 'Jasmine Park',
      company: 'buyer — direct',
      email: 'jasmine.p@gmail.com',
      role: 'Buyer',
      shared_at: '5d ago',
      last_viewed: '3d ago',
      docs_opened: 8,
      docs_total: 21,
      has_all_docs: false,
      missing_docs: ['Sewer Lateral Video.pdf', 'Fire Insurance Declarations.pdf'],
    },
  ];

  const requests: InboundRequest[] = [
    {
      id: 'req1',
      from_name: 'Luis Rodriguez',
      from_company: 'Keller Williams',
      type: 'sign',
      doc_name: 'Counter-offer #2',
      description: 'Asking you to review and sign the revised counter-offer with $4.20M plus seller credit.',
      requested_at: '2h ago',
      due: 'Today, 6pm',
      urgency: 'high',
    },
    {
      id: 'req2',
      from_name: 'Kim Chen',
      from_company: 'Compass',
      type: 'fill',
      doc_name: 'Request for Repairs (RFR)',
      description: 'Needs your seller\'s position on 4 repair items before the contingency deadline.',
      requested_at: '1d ago',
      due: 'Fri, Apr 19',
      urgency: 'medium',
    },
    {
      id: 'req3',
      from_name: 'Michael Ng',
      from_company: 'Wells Fargo',
      type: 'upload',
      doc_name: 'Seller\'s Statement of Information',
      description: 'Lender needs the signed SSI to complete underwriting.',
      requested_at: '2d ago',
      due: 'Mon, Apr 22',
      urgency: 'low',
    },
  ];

  const recipientsMissingDocs = recipients.filter(r => !r.has_all_docs);
  const urgencyColor: Record<InboundRequest['urgency'], { bg: string; text: string; label: string }> = {
    high: { bg: 'bg-brand-critical-bg', text: 'text-brand-critical', label: 'Due today' },
    medium: { bg: 'bg-brand-moderate-bg', text: 'text-brand-moderate', label: 'This week' },
    low: { bg: 'bg-brand-info-bg', text: 'text-brand-info', label: 'Later' },
  };
  const requestTypeVerb: Record<InboundRequest['type'], string> = {
    sign: 'Sign',
    fill: 'Fill out',
    upload: 'Upload',
    respond: 'Respond to',
  };

  // Demo role gate — only listing / co-listing agents can invite new recipients.
  const demoRole =
    (typeof window !== 'undefined' && window.localStorage.getItem('discloser.demo.role')) ||
    'listing_agent';
  const canInvite = LISTING_AGENT_ROLES.has(demoRole);

  // Invite modal state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<InviteRoleValue>('buying_agent');
  const [inviteMessage, setInviteMessage] = useState('');
  const [visibility, setVisibility] = useState<Record<VisibilityKey, boolean>>(() =>
    defaultVisibilityFor('buying_agent'),
  );

  // When the role changes, reset visibility defaults to what makes sense for the new role.
  useEffect(() => {
    setVisibility(defaultVisibilityFor(inviteRole));
  }, [inviteRole]);

  const canSendInvite = inviteEmail.trim().length > 0 && !!inviteRole;

  const toggleVisibility = (key: VisibilityKey) =>
    setVisibility(v => ({ ...v, [key]: !v[key] }));

  const resetAndClose = () => {
    setInviteOpen(false);
    // Reset after a tick so the modal doesn't flicker content on close.
    setTimeout(() => {
      setInviteEmail('');
      setInviteMessage('');
      setInviteRole('buying_agent');
      setVisibility(defaultVisibilityFor('buying_agent'));
    }, 200);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-semibold">Share</h2>
          <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0">
            {recipients.length} recipients
          </Badge>
          <span className="font-meta text-xs text-brand-text-3">
            {requests.length} inbound · {recipientsMissingDocs.length} need updates
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-pill border-black/10 font-meta">
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Preview as client
          </Button>
          {canInvite && (
            <Button
              size="sm"
              onClick={() => setInviteOpen(true)}
              className="rounded-pill bg-brand-ink hover:bg-brand-ink-soft text-white font-meta"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add recipient
            </Button>
          )}
        </div>
      </div>

      {/* Requests FROM others — top priority inbox */}
      {requests.length > 0 && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-brand-text-3" />
            <h3 className="font-display text-lg font-semibold">Requests from others</h3>
            <Badge className="bg-brand-critical-bg text-brand-critical font-meta border-0 text-[10px]">
              {requests.length} pending
            </Badge>
          </div>

          <div className="space-y-2">
            {requests.map(req => {
              const u = urgencyColor[req.urgency];
              return (
                <div
                  key={req.id}
                  className="bg-white rounded-xl border border-black/5 p-4 hover:border-black/10 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-full bg-brand-paper flex items-center justify-center shrink-0">
                      <span className="font-meta text-xs font-semibold text-brand-text-2">
                        {req.from_name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 mb-0.5 flex-wrap">
                        <span className="font-semibold text-sm">
                          {req.from_name}
                        </span>
                        <span className="font-meta text-[11px] text-brand-text-3">
                          {req.from_company}
                        </span>
                        <span className="font-meta text-[11px] text-brand-text-3">·</span>
                        <span className="font-meta text-[11px] text-brand-text-3">
                          {req.requested_at}
                        </span>
                        <span className={`font-meta text-[10px] uppercase tracking-wider font-semibold ${u.text} ml-auto`}>
                          {u.label}
                        </span>
                      </div>
                      <div className="text-sm mb-1">
                        <span className="font-medium">{requestTypeVerb[req.type]}:</span>{' '}
                        <span className="text-brand-ink">{req.doc_name}</span>
                      </div>
                      <p className="text-xs text-brand-text-2 leading-relaxed mb-3">{req.description}</p>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className={`rounded-pill font-meta text-xs h-7 ${
                            req.urgency === 'high'
                              ? 'bg-brand-critical hover:bg-brand-critical/90 text-white'
                              : 'bg-brand-ink hover:bg-brand-ink-soft text-white'
                          }`}
                        >
                          {req.type === 'sign' && 'Review &amp; sign'}
                          {req.type === 'fill' && 'Open form'}
                          {req.type === 'upload' && 'Upload doc'}
                          {req.type === 'respond' && 'Respond'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-pill font-meta text-xs h-7 text-brand-text-3"
                        >
                          Ask Closer
                        </Button>
                        <span className="font-meta text-[11px] text-brand-text-3 ml-auto">
                          Due {req.due}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Pending pushes — new docs not yet sent to everyone */}
      {recipientsMissingDocs.length > 0 && (
        <section className="mb-6">
          <div className="bg-gradient-to-br from-brand-sky/25 via-brand-cream/25 to-brand-paper border border-black/5 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0">
                <AlertCircle className="w-4 h-4 text-brand-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-semibold text-sm">
                    2 docs haven't reached everyone
                  </span>
                  <span className="font-meta text-[11px] text-brand-text-3">
                    · uploaded in the last 3 days
                  </span>
                </div>
                <p className="text-xs text-brand-text-2 leading-relaxed mb-4">
                  You've added new documents since the last share update. Push them to each recipient
                  who's missing them.
                </p>

                <div className="space-y-2 mb-4">
                  <div className="bg-white/70 rounded-xl border border-black/5 px-4 py-3">
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-3.5 h-3.5 text-brand-text-3 shrink-0" />
                        <span className="font-semibold text-sm truncate">
                          Fire Insurance Declarations.pdf
                        </span>
                      </div>
                      <span className="font-meta text-[11px] text-brand-text-3 shrink-0">
                        Missing for 3
                      </span>
                    </div>
                    <div className="font-meta text-[11px] text-brand-text-3 ml-5.5">
                      Kim Chen · Luis Rodriguez · Jasmine Park
                    </div>
                  </div>

                  <div className="bg-white/70 rounded-xl border border-black/5 px-4 py-3">
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-3.5 h-3.5 text-brand-text-3 shrink-0" />
                        <span className="font-semibold text-sm truncate">
                          Sewer Lateral Video.pdf
                        </span>
                      </div>
                      <span className="font-meta text-[11px] text-brand-text-3 shrink-0">
                        Missing for 2
                      </span>
                    </div>
                    <div className="font-meta text-[11px] text-brand-text-3 ml-5.5">
                      Kim Chen · Jasmine Park
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button size="sm" className="rounded-pill bg-brand-ink hover:bg-brand-ink-soft text-white font-meta">
                    <ArrowRight className="w-3.5 h-3.5 mr-1.5" /> Push both to all 3 recipients
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-pill font-meta text-xs text-brand-text-3">
                    Choose recipients...
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Recipients */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CircleDot className="w-4 h-4 text-brand-text-3" />
            <h3 className="font-display text-lg font-semibold">Recipients</h3>
            <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0">
              {recipients.length}
            </Badge>
          </div>
          <div className="flex gap-1 font-meta text-xs">
            <FilterChip active>All</FilterChip>
            <FilterChip>Active</FilterChip>
            <FilterChip>Not yet viewed</FilterChip>
          </div>
        </div>

        <div data-testid="recipients-list" className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          {recipients.map((r, i) => {
            const viewedPct = r.docs_total > 0 ? (r.docs_opened / r.docs_total) * 100 : 0;
            return (
              <div
                key={r.id}
                data-testid={`recipient-item-${r.id}`}
                className={`px-5 py-4 flex items-center gap-4 ${
                  i !== recipients.length - 1 ? 'border-b border-black/5' : ''
                } hover:bg-brand-paper-warm/40 transition-colors`}
              >
                <div className="w-9 h-9 rounded-full bg-brand-paper flex items-center justify-center shrink-0">
                  <span className="font-meta text-xs font-semibold text-brand-text-2">
                    {r.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 mb-0.5">
                    <span className="font-semibold text-sm">{r.name}</span>
                    <span className="font-meta text-[11px] text-brand-text-3">{r.company}</span>
                    <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0 text-[10px]">
                      {r.role}
                    </Badge>
                    {!r.has_all_docs && (
                      <Badge className="bg-brand-moderate-bg text-brand-moderate font-meta border-0 text-[10px]">
                        Missing {r.missing_docs?.length} doc{r.missing_docs?.length === 1 ? '' : 's'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 font-meta text-[11px] text-brand-text-3">
                    <span>Shared {r.shared_at}</span>
                    <span>·</span>
                    {r.last_viewed ? (
                      <span className="inline-flex items-center gap-1 text-brand-info">
                        <CheckCircle2 className="w-3 h-3" /> Last viewed {r.last_viewed}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-brand-text-3">
                        <Clock className="w-3 h-3" /> Not yet viewed
                      </span>
                    )}
                  </div>
                </div>

                {/* Doc engagement bar */}
                <div className="w-[140px] shrink-0">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3">
                      Opened
                    </span>
                    <span className="font-brand text-xs font-semibold tabular-nums">
                      {r.docs_opened} / {r.docs_total}
                    </span>
                  </div>
                  <div className="h-1.5 bg-black/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-ink rounded-full"
                      style={{ width: `${viewedPct}%` }}
                    />
                  </div>
                </div>

                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" className="rounded-pill font-meta text-xs h-8">
                    Activity
                  </Button>
                  <Button variant="ghost" size="icon" className="w-8 h-8 rounded-full">
                    <MoreHorizontal className="w-4 h-4 text-brand-text-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Activity log */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-brand-text-3" />
          <h3 className="font-display text-lg font-semibold">Activity</h3>
        </div>
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          <ShareActivityRow
            who="Sarah Chen (Sotheby's)"
            action="opened Structural Engineer Report.pdf"
            when="4h ago"
            tone="info"
          />
          <ShareActivityRow
            who="Kim Chen (Compass)"
            action="viewed the property page"
            when="2h ago"
          />
          <ShareActivityRow
            who="Luis Rodriguez (KW)"
            action="requested you sign Counter-offer #2"
            when="2h ago"
            tone="critical"
          />
          <ShareActivityRow
            who="You"
            action="uploaded Fire Insurance Declarations.pdf"
            when="2m ago"
          />
          <ShareActivityRow
            who="Michael Ng (Wells Fargo)"
            action="received share invitation"
            when="2d ago"
          />
          <ShareActivityRow who="You" action="made the listing public" when="5d ago" />
        </div>
      </section>

      {/* Invite recipient modal — houses both the invite flow and the link/visibility options */}
      <Dialog
        open={inviteOpen}
        onOpenChange={next => {
          if (!next) resetAndClose();
          else setInviteOpen(true);
        }}
      >
        <DialogPortal>
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
          <DialogPrimitive.Content
            className="fixed left-[50%] top-[50%] z-50 w-full max-w-[640px] translate-x-[-50%] translate-y-[-50%] rounded-2xl border border-black/5 bg-white shadow-xl font-brand duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 outline-none"
          >
            {/* Header */}
            <div className="px-7 pt-7 pb-5">
              <DialogPrimitive.Title asChild>
                <h2 className="font-display text-2xl font-semibold leading-tight">
                  Invite someone
                </h2>
              </DialogPrimitive.Title>
              <DialogPrimitive.Description asChild>
                <p className="font-meta text-xs text-brand-text-3 mt-1">
                  They'll get an email with a link to this property's packet.
                </p>
              </DialogPrimitive.Description>
            </div>

            {/* Section A — Who */}
            <div className="px-7 pb-5 space-y-4">
              <div>
                <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3 mb-2">
                  Who
                </div>

                <div className="space-y-3">
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="name@brokerage.com"
                    className="rounded-pill bg-brand-paper-warm border-black/10 focus-visible:bg-white h-10 px-4 font-brand"
                  />

                  <Select value={inviteRole} onValueChange={v => setInviteRole(v as InviteRoleValue)}>
                    <SelectTrigger className="rounded-pill border-black/10 font-brand bg-brand-paper-warm focus-visible:bg-white h-auto py-2.5 pl-4">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {INVITE_ROLE_OPTIONS.map(r => (
                        <SelectItem
                          key={r.value}
                          value={r.value}
                          className="py-2.5 pl-8 pr-3 cursor-pointer focus:bg-brand-paper-warm"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.dot}`} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline gap-2">
                                <span className="text-sm font-semibold">{r.label}</span>
                                <span className="font-meta text-[11px] text-brand-text-3">
                                  {r.sub}
                                </span>
                              </div>
                              <div className="font-meta text-[11px] text-brand-text-3 mt-0.5">
                                {r.note}
                              </div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <textarea
                    value={inviteMessage}
                    onChange={e => setInviteMessage(e.target.value)}
                    rows={3}
                    placeholder="Add a note (optional)..."
                    className="w-full rounded-xl border border-black/10 bg-white px-4 py-2.5 text-sm font-brand placeholder:text-brand-text-3/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink/10 resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Section B — What they'll see */}
            <div className="border-t border-black/5 px-7 pt-5 pb-2">
              <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3 mb-3">
                What they'll see
              </div>

              {/* Public link row */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold">Public link</span>
                  <Badge className="bg-brand-sky/30 text-brand-info font-meta border-0 text-[10px]">
                    Active
                  </Badge>
                </div>
                <div className="flex gap-2 mb-1.5">
                  <div className="flex-1 bg-brand-paper-warm rounded-pill px-4 py-2 font-mono text-xs text-brand-text-2 truncate border border-black/5">
                    discloser.co/l/pacific-ave-2847
                  </div>
                  <Button variant="outline" size="sm" className="rounded-pill border-black/10 font-meta">
                    Copy
                  </Button>
                </div>
                <div className="font-brand text-[11px] text-brand-text-3 tabular-nums">
                  Viewed 47 times by 12 unique visitors
                </div>
              </div>

              {/* Visibility toggles */}
              <div>
                <div className="font-meta text-[11px] text-brand-text-3 mb-2">
                  Defaults for a {INVITE_ROLE_LABELS[inviteRole]}
                </div>
                <div className="space-y-1">
                  <VisibilityRow
                    label="Findings & severity"
                    enabled={visibility.findings}
                    onToggle={() => toggleVisibility('findings')}
                  />
                  <VisibilityRow
                    label="Closer's summary"
                    enabled={visibility.summaries}
                    onToggle={() => toggleVisibility('summaries')}
                  />
                  <VisibilityRow
                    label="Source documents (PDFs)"
                    enabled={visibility.documents}
                    onToggle={() => toggleVisibility('documents')}
                  />
                  <VisibilityRow
                    label="Offers on this listing"
                    enabled={visibility.offers}
                    onToggle={() => toggleVisibility('offers')}
                  />
                  <VisibilityRow
                    label="Who else is on this listing"
                    enabled={visibility.participants}
                    onToggle={() => toggleVisibility('participants')}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-7 py-4 border-t border-black/5 bg-brand-paper-warm/40 rounded-b-2xl">
              <Button
                variant="ghost"
                size="sm"
                onClick={resetAndClose}
                className="rounded-pill font-meta text-xs text-brand-text-2"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!canSendInvite}
                onClick={() => {
                  // eslint-disable-next-line no-console
                  console.log('[invite]', {
                    email: inviteEmail,
                    role: inviteRole,
                    message: inviteMessage,
                    visibility,
                  });
                  resetAndClose();
                }}
                className="rounded-pill bg-brand-ink hover:bg-brand-ink-soft text-white font-meta disabled:opacity-50"
              >
                Send invitation
              </Button>
            </div>

            {/* Close X */}
            <DialogPrimitive.Close
              className="absolute right-4 top-4 w-8 h-8 rounded-full flex items-center justify-center text-brand-text-3 hover:bg-brand-paper-warm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-ink/10"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </div>
  );
}

function VisibilityRow({
  label,
  enabled,
  onToggle,
}: {
  label: string;
  enabled: boolean;
  onToggle?: () => void;
}) {
  const inner = (
    <>
      <span className="text-sm text-brand-text-2">{label}</span>
      <div
        className={`w-8 h-4 rounded-full relative transition-colors ${
          enabled ? 'bg-brand-ink' : 'bg-black/10'
        }`}
      >
        <div
          className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
            enabled ? 'left-[18px]' : 'left-0.5'
          }`}
        />
      </div>
    </>
  );

  if (onToggle) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between py-1 text-left rounded-md px-1 hover:bg-brand-paper-warm/60 transition-colors"
      >
        {inner}
      </button>
    );
  }

  return <div className="flex items-center justify-between py-1">{inner}</div>;
}

function ShareActivityRow({
  who,
  action,
  when,
  tone,
}: {
  who: string;
  action: string;
  when: string;
  tone?: 'critical' | 'info';
}) {
  const dotColor =
    tone === 'critical' ? 'bg-brand-critical' : tone === 'info' ? 'bg-brand-info' : 'bg-brand-text-3';
  return (
    <div className="px-5 py-3 flex items-center gap-3 border-b border-black/5 last:border-0">
      <div className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`} />
      <div className="flex-1 min-w-0 text-sm">
        <span className="font-medium">{who}</span>
        <span className="text-brand-text-2"> {action}</span>
      </div>
      <span className="font-meta text-[11px] text-brand-text-3 shrink-0">{when}</span>
    </div>
  );
}

/* ============================================================
   VIEW — Permits (Shovels.ai data + disclosure cross-check)
   ============================================================ */

function ViewPermits() {
  type PermitStatus = 'final' | 'issued' | 'in_review' | 'expired' | 'withdrawn';
  type PermitMatch = 'confirmed' | 'undisclosed' | 'unpermitted' | 'mismatch';

  type Permit = {
    id: string;
    number: string;
    description: string;
    type: 'Building' | 'Electrical' | 'Mechanical' | 'Plumbing' | 'Roofing' | 'Solar';
    status: PermitStatus;
    issue_date: string;
    final_date?: string;
    job_value: number;
    contractor: string;
    jurisdiction: string;
    match: PermitMatch;
    match_note?: string;
  };

  const permits: Permit[] = [
    {
      id: 'p1',
      number: 'RES-E-19-00142',
      description: '200A electrical panel upgrade',
      type: 'Electrical',
      status: 'final',
      issue_date: '2019-03-14',
      final_date: '2019-04-02',
      job_value: 4200,
      contractor: 'Bay Area Electric, Inc.',
      jurisdiction: 'SF DBI',
      match: 'confirmed',
      match_note: 'Matches TDS Section 3.2 disclosure',
    },
    {
      id: 'p2',
      number: 'RES-R-21-00887',
      description: 'Re-roof, composition shingle, 30-yr warranty',
      type: 'Roofing',
      status: 'final',
      issue_date: '2021-05-10',
      final_date: '2021-06-18',
      job_value: 18500,
      contractor: 'Pacific Coast Roofing Co.',
      jurisdiction: 'SF DBI',
      match: 'confirmed',
      match_note: 'Matches seller disclosure — Home Inspection pg 18',
    },
    {
      id: 'p3',
      number: 'RES-S-20-00314',
      description: 'Solar PV install, 6.4 kW, 14 panels',
      type: 'Solar',
      status: 'final',
      issue_date: '2020-08-21',
      final_date: '2020-09-15',
      job_value: 22800,
      contractor: 'Sunrun Solar',
      jurisdiction: 'SF DBI',
      match: 'undisclosed',
      match_note: 'Solar system is on the property but NOT mentioned in the TDS',
    },
    {
      id: 'p4',
      number: 'RES-B-18-04422',
      description: 'Kitchen remodel — cabinets, counters, island',
      type: 'Building',
      status: 'final',
      issue_date: '2018-06-02',
      final_date: '2018-11-20',
      job_value: 48000,
      contractor: 'Goldthwaite Construction',
      jurisdiction: 'SF DBI',
      match: 'confirmed',
    },
    {
      id: 'p5',
      number: 'RES-P-15-01109',
      description: 'Sewer lateral replacement — front yard to main',
      type: 'Plumbing',
      status: 'final',
      issue_date: '2015-04-08',
      final_date: '2015-05-12',
      job_value: 12400,
      contractor: 'San Francisco Plumbing Works',
      jurisdiction: 'SF DBI',
      match: 'mismatch',
      match_note: 'NHD says compliance cert NOT on file — permit exists from 2015',
    },
    {
      id: 'p6',
      number: 'RES-M-22-03014',
      description: 'Water heater replacement, tankless',
      type: 'Mechanical',
      status: 'final',
      issue_date: '2022-11-04',
      final_date: '2022-11-22',
      job_value: 3900,
      contractor: 'Homeowner (DIY)',
      jurisdiction: 'SF DBI',
      match: 'confirmed',
    },
    {
      id: 'p7',
      number: 'RES-B-12-00912',
      description: 'Second-floor bathroom addition',
      type: 'Building',
      status: 'final',
      issue_date: '2012-09-14',
      final_date: '2013-02-20',
      job_value: 32000,
      contractor: 'Oakdale Builders LLC',
      jurisdiction: 'SF DBI',
      match: 'confirmed',
    },
    {
      id: 'p8',
      number: 'RES-B-08-02417',
      description: 'Seismic retrofit, foundation bolting',
      type: 'Building',
      status: 'final',
      issue_date: '2008-07-21',
      final_date: '2008-09-01',
      job_value: 8500,
      contractor: 'Earthquake Safety Experts',
      jurisdiction: 'SF DBI',
      match: 'confirmed',
    },
    {
      id: 'p9',
      number: 'RES-B-23-00218',
      description: 'Rear deck replacement, pressure-treated lumber',
      type: 'Building',
      status: 'in_review',
      issue_date: '2023-02-11',
      job_value: 14500,
      contractor: 'Urban Outdoor Works',
      jurisdiction: 'SF DBI',
      match: 'undisclosed',
      match_note: 'Application in review — not yet final, not in TDS',
    },
    {
      id: 'p10',
      number: 'RES-E-23-01882',
      description: 'EV charger install, Level 2, garage',
      type: 'Electrical',
      status: 'final',
      issue_date: '2023-10-05',
      final_date: '2023-10-18',
      job_value: 2400,
      contractor: 'ChargePoint Certified Installer',
      jurisdiction: 'SF DBI',
      match: 'confirmed',
    },
  ];

  // Mismatches in the seller's story: work claimed that has no permit
  const unpermittedClaims = [
    {
      claim: 'Bathroom renovation, 2017',
      source: 'TDS Section 4.1',
      note: 'Seller disclosed a 2017 primary bath renovation. No corresponding permit on file.',
    },
  ];

  const totalPermits = permits.length;
  const totalValue = permits.reduce((s, p) => s + p.job_value, 0);
  const confirmedCount = permits.filter(p => p.match === 'confirmed').length;
  const undisclosedCount = permits.filter(p => p.match === 'undisclosed').length;
  const mismatchCount = permits.filter(p => p.match === 'mismatch').length;
  const flagCount = undisclosedCount + mismatchCount + unpermittedClaims.length;

  const statusColor: Record<PermitStatus, { bg: string; text: string; label: string }> = {
    final: { bg: 'bg-brand-info-bg', text: 'text-brand-info', label: 'Final' },
    issued: { bg: 'bg-brand-sky-soft', text: 'text-brand-ink', label: 'Issued' },
    in_review: { bg: 'bg-brand-moderate-bg', text: 'text-brand-moderate', label: 'In review' },
    expired: { bg: 'bg-brand-critical-bg', text: 'text-brand-critical', label: 'Expired' },
    withdrawn: { bg: 'bg-brand-paper', text: 'text-brand-text-3', label: 'Withdrawn' },
  };

  const matchColor: Record<PermitMatch, { bg: string; text: string; dot: string; label: string }> = {
    confirmed: { bg: 'bg-brand-info-bg', text: 'text-brand-info', dot: 'bg-brand-info', label: 'Confirmed' },
    undisclosed: { bg: 'bg-brand-moderate-bg', text: 'text-brand-moderate', dot: 'bg-brand-moderate', label: 'Undisclosed' },
    unpermitted: { bg: 'bg-brand-critical-bg', text: 'text-brand-critical', dot: 'bg-brand-critical', label: 'Unpermitted' },
    mismatch: { bg: 'bg-brand-high-bg', text: 'text-brand-high', dot: 'bg-brand-high', label: 'Mismatch' },
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-semibold">Permits</h2>
          <Badge className="bg-brand-paper text-brand-text-2 font-meta border-0">
            {totalPermits} on file
          </Badge>
          <span className="font-meta text-xs text-brand-text-3">
            {flagCount} don't match the seller's disclosure
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-pill border-black/10 font-meta">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export
          </Button>
          <Button size="sm" variant="ghost" className="rounded-pill font-meta text-xs text-brand-text-3">
            Data via Shovels.ai · synced 4h ago
          </Button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 md:mb-5">
        <StatTile label="Total permits" value={totalPermits} />
        <StatTile label="Total job value" value={formatMoney(totalValue)} />
        <StatTile label="Disclosure flags" value={flagCount} tone="moderate" />
        <StatTile label="Last permit" value="Oct 2023" />
      </div>

      {/* Hero differential insight — the moat */}
      {flagCount > 0 && (
        <div className="mb-6 bg-gradient-to-br from-brand-moderate-bg/60 via-brand-cream/30 to-brand-paper border border-brand-moderate/20 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-brand-moderate" />
            </div>
            <div className="flex-1">
              <div className="font-meta text-[11px] uppercase tracking-wider text-brand-moderate font-semibold mb-1">
                Closer's cross-check
              </div>
              <div className="font-semibold text-sm mb-1">
                The permit record tells a different story than the disclosure
              </div>
              <p className="text-xs text-brand-text-2 leading-relaxed mb-3">
                Out of {totalPermits} permits on file, <strong>{confirmedCount}</strong> match what the
                seller disclosed, but{' '}
                <strong>{undisclosedCount} work{undisclosedCount === 1 ? '' : 's'}</strong> were done{' '}
                <em>without being disclosed</em>, <strong>{mismatchCount}</strong> contradict the
                disclosure, and <strong>{unpermittedClaims.length}</strong> claimed improvement has no
                permit on file. Treat these as negotiation points.
              </p>
              <div className="flex gap-2 flex-wrap">
                <SuggestedAction small>Draft a question list for seller</SuggestedAction>
                <SuggestedAction small>Compare with TDS</SuggestedAction>
                <SuggestedAction small>Explain to my client</SuggestedAction>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter row */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className="font-meta text-[11px] uppercase tracking-wider text-brand-text-3 self-center mr-2">
          Match
        </span>
        <FilterChip active>All · {totalPermits}</FilterChip>
        <FilterChip>Confirmed · {confirmedCount}</FilterChip>
        <FilterChip>Undisclosed · {undisclosedCount}</FilterChip>
        <FilterChip>Mismatch · {mismatchCount}</FilterChip>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5">
        <span className="font-meta text-[11px] uppercase tracking-wider text-brand-text-3 self-center mr-2">
          Type
        </span>
        {['All', 'Building', 'Electrical', 'Mechanical', 'Plumbing', 'Roofing', 'Solar'].map(
          (t, i) => (
            <FilterChip key={t} active={i === 0}>
              {t}
            </FilterChip>
          )
        )}
      </div>

      {/* Unpermitted claims — at top (biggest risk) */}
      {unpermittedClaims.length > 0 && (
        <div className="mb-3">
          {unpermittedClaims.map((c, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-brand-critical/20 p-4 hover:border-brand-critical/40 cursor-pointer transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0 bg-brand-critical" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2.5 mb-1 flex-wrap">
                    <span className="font-semibold text-[15px]">{c.claim}</span>
                    <span className="font-meta text-[10px] uppercase tracking-wider font-semibold text-brand-critical">
                      Unpermitted
                    </span>
                    <span className="font-meta text-[10px] text-brand-text-3 inline-flex items-center gap-1 bg-brand-paper px-1.5 py-0.5 rounded">
                      <FileText className="w-3 h-3" />
                      {c.source}
                    </span>
                  </div>
                  <p className="text-sm text-brand-text-2 leading-relaxed mb-2">{c.note}</p>
                  <div className="font-meta text-[11px] text-brand-critical flex items-center gap-1">
                    <XCircle className="w-3 h-3" />
                    No permit on file for this work
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Permit list */}
      <div className="space-y-2">
        {permits.map(p => {
          const st = statusColor[p.status];
          const mc = matchColor[p.match];
          return (
            <div
              key={p.id}
              className="bg-white rounded-xl border border-black/5 p-4 hover:border-black/10 cursor-pointer transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${mc.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2.5 mb-1 flex-wrap">
                    <span className="font-mono text-[11px] text-brand-text-3">{p.number}</span>
                    <span className="font-semibold text-[15px]">{p.description}</span>
                    <Badge className={`${st.bg} ${st.text} font-meta border-0 text-[10px]`}>
                      {st.label}
                    </Badge>
                    <span className={`font-meta text-[10px] uppercase tracking-wider font-semibold ${mc.text}`}>
                      {mc.label}
                    </span>
                    <span className="ml-auto font-brand font-semibold text-base shrink-0 tabular-nums">
                      {formatMoney(p.job_value)}
                    </span>
                  </div>

                  {p.match_note && (
                    <p className="text-sm text-brand-text-2 leading-relaxed mb-2">{p.match_note}</p>
                  )}

                  <div className="flex items-center gap-4 font-meta text-[11px] text-brand-text-3 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Hammer className="w-3 h-3" />
                      {p.type}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Issued {new Date(p.issue_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                    </span>
                    {p.final_date && (
                      <span>Finaled {new Date(p.final_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
                    )}
                    <span>{p.contractor}</span>
                    <span className="ml-auto">{p.jurisdiction}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 font-meta text-[11px] text-brand-text-3 text-center">
        <Lock className="w-3 h-3 inline -mt-0.5 mr-1" />
        Data sourced from public records via Shovels.ai · Cross-checks generated by Closer · Last synced 4h ago
      </div>
    </div>
  );
}

/* ============================================================
   CHAT MODE — chat as primary surface inside C's chrome
   ============================================================ */

function ChatMode({
  activeSection,
  setSection,
  sidebarCollapsed,
  toggleSidebar,
}: PanelProps) {
  const p = currentProperty;
  const isDesktop = useIsDesktop();
  return (
    <>
      <HeroBand />
      <MobileSectionPills activeSection={activeSection} setSection={setSection} />
      <div
        className="max-w-[1440px] mx-auto px-4 md:px-6 py-4 md:py-8 grid gap-4 md:gap-6 lg:gap-8 lg:transition-[grid-template-columns] lg:duration-300 grid-cols-1"
        style={
          isDesktop
            ? { gridTemplateColumns: gridCols(sidebarCollapsed) }
            : undefined
        }
      >
        {/* Section nav — hidden on mobile (handled by MobileSectionPills at top) */}
        <div className="hidden lg:block">
          <SectionNav
            active={activeSection}
            setSection={setSection}
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
          />
        </div>

        {/* Main = chat thread (expanded from B) */}
        <div className="min-w-0">
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 220px)' }}>
            <div className="px-5 py-3 border-b border-black/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-sky to-brand-cream flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-brand-ink" />
                </div>
                <div>
                  <div className="font-display text-base font-semibold leading-tight">Closer</div>
                  <div className="font-meta text-[10px] text-brand-text-3">
                    Reading 2847 Pacific Ave · {p.pages_analyzed} pages
                  </div>
                </div>
              </div>
              <button className="font-meta text-[11px] text-brand-accent hover:underline">
                Clear thread
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* System ribbon */}
              <div className="flex justify-center">
                <div className="px-4 py-1.5 rounded-pill bg-brand-paper font-meta text-[11px] text-brand-text-3">
                  Uploaded 23 documents · Analyzed in 4m 12s
                </div>
              </div>

              {/* Closer summary */}
              <ChatClosurBubble>
                <p className="mb-3">
                  I finished reading the packet. Here's what matters for <strong>{p.address_line}</strong>:
                </p>
                <p className="mb-4 text-brand-text-2">{p.summary_lede}</p>

                <div className="bg-brand-paper-warm rounded-xl border border-black/5 overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-black/5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ClipboardCheck className="w-4 h-4 text-brand-text-3" />
                      <span className="font-semibold text-sm">Material findings</span>
                    </div>
                    <span className="font-meta text-[11px] text-brand-text-3">
                      {formatMoney(p.est_repair_low)}–{formatMoney(p.est_repair_high)}
                    </span>
                  </div>
                  {p.findings
                    .filter(f => f.severity !== 'info')
                    .map((f, idx, arr) => {
                      const s = sevStyles[f.severity];
                      return (
                        <div
                          key={f.id}
                          className={`px-4 py-2.5 flex items-start gap-3 hover:bg-white cursor-pointer transition-colors ${
                            idx !== arr.length - 1 ? 'border-b border-black/5' : ''
                          }`}
                        >
                          <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${s.dot}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="font-medium text-sm">{f.title}</span>
                              <span className={`font-meta text-[10px] uppercase tracking-wider font-semibold ${s.text}`}>
                                {s.label}
                              </span>
                            </div>
                            <div className="font-meta text-[11px] text-brand-text-3">
                              {f.cited_at}
                              {f.est_low > 0 && ` · ${formatMoney(f.est_low)}–${formatMoney(f.est_high)}`}
                            </div>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-brand-text-3 mt-1" />
                        </div>
                      );
                    })}
                </div>

                <div className="mt-4 flex gap-2 flex-wrap">
                  <SuggestedAction>Draft a credit ask for HVAC</SuggestedAction>
                  <SuggestedAction>Summarize for my client</SuggestedAction>
                  <SuggestedAction>What's the single biggest risk?</SuggestedAction>
                </div>
              </ChatClosurBubble>

              <ChatUserBubble>What's the single biggest risk?</ChatUserBubble>

              <ChatClosurBubble>
                <p className="mb-3">
                  Foundation settlement at the northeast corner. <strong>7/8" differential</strong> with
                  visible diagonal cracking. I'd engage a structural engineer before dropping contingency.
                </p>
                <div className="bg-brand-critical-bg/40 border border-brand-critical/20 rounded-xl p-3.5 mb-3 max-w-md">
                  <div className="flex items-start gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-critical mt-1.5 shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="font-semibold text-sm">Foundation settlement, NE corner</span>
                        <span className="font-brand text-sm font-semibold tabular-nums">$12k–$18k</span>
                      </div>
                      <p className="text-xs text-brand-text-2 leading-relaxed mb-2">
                        7/8" differential with visible diagonal cracking in the basement wall.
                      </p>
                      <button className="font-meta text-[11px] text-brand-accent hover:underline">
                        Structural Engineer Report · pg 87 →
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-brand-text-2">
                  The two moderates (HVAC, retaining wall) are cost-bearing but negotiable. Want me to
                  draft the contingency language?
                </p>
              </ChatClosurBubble>
            </div>

            {/* Input */}
            <div data-testid="chat-input-row" className="px-5 pb-4 pt-2 border-t border-black/5 bg-brand-paper-warm/50">
              <div className="bg-white rounded-3xl border border-black/10 shadow-sm p-2 pl-4 flex items-end gap-2">
                <button className="w-8 h-8 rounded-full hover:bg-brand-paper flex items-center justify-center text-brand-text-3">
                  <Paperclip className="w-4 h-4" />
                </button>
                <Input
                  placeholder="Ask Closer anything about this property..."
                  className="flex-1 border-0 shadow-none focus-visible:ring-0 font-brand text-sm bg-transparent"
                />
                <button className="w-8 h-8 rounded-full bg-brand-ink hover:bg-brand-ink-soft text-white flex items-center justify-center">
                  <ArrowUp className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

/* ============================================================
   SHARED PRIMITIVES
   ============================================================ */

function NavLink({ children, active }: { children: React.ReactNode; active?: boolean }) {
  return (
    <button
      className={`px-3 py-1.5 rounded-pill flex items-center gap-1.5 transition-colors ${
        active
          ? 'bg-brand-paper text-brand-ink font-medium'
          : 'text-brand-text-2 hover:bg-brand-paper/60'
      }`}
    >
      {children}
    </button>
  );
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: Severity;
}) {
  const color = tone ? sevStyles[tone].text : 'text-brand-ink';
  return (
    <div className="bg-white rounded-xl border border-black/5 p-4">
      <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3 mb-1.5">
        {label}
      </div>
      <div className={`font-brand text-2xl font-semibold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-brand-text-3">{k}</span>
      <span className="text-brand-ink font-medium">{v}</span>
    </div>
  );
}

function FilterChip({ children, active }: { children: React.ReactNode; active?: boolean }) {
  return (
    <button
      className={`px-3 py-1 rounded-pill font-meta transition-colors ${
        active
          ? 'bg-brand-ink text-white'
          : 'bg-white text-brand-text-2 border border-black/5 hover:bg-brand-paper'
      }`}
    >
      {children}
    </button>
  );
}

function ActivityRow({ label, time }: { label: string; time: string }) {
  return (
    <div className="flex items-start gap-2">
      <CircleDot className="w-3 h-3 text-brand-text-3 mt-1 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-brand-text-2 leading-tight">{label}</div>
        <div className="font-meta text-[10px] text-brand-text-3 mt-0.5">{time}</div>
      </div>
    </div>
  );
}

function SuggestedAction({ children, small }: { children: React.ReactNode; small?: boolean }) {
  return (
    <button
      className={`rounded-pill border border-black/10 hover:bg-white hover:border-black/20 bg-white/70 font-meta text-brand-text-2 transition-colors ${
        small ? 'px-3 py-1.5 text-[11px]' : 'px-3.5 py-1.5 text-xs'
      }`}
    >
      {children}
    </button>
  );
}

function ChatClosurBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-sky to-brand-cream flex items-center justify-center shrink-0">
        <Sparkles className="w-4 h-4 text-brand-ink" />
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="font-semibold text-sm">Closer</span>
          <span className="font-meta text-[10px] text-brand-text-3">just now</span>
        </div>
        <div className="text-[14px] leading-relaxed text-brand-ink">{children}</div>
      </div>
    </div>
  );
}

function ChatUserBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-3 justify-end">
      <div className="max-w-[75%] bg-brand-paper rounded-3xl rounded-br-lg px-4 py-3 text-[14px] leading-relaxed">
        {children}
      </div>
      <div className="w-8 h-8 rounded-full bg-brand-ink text-white text-xs font-meta font-semibold flex items-center justify-center shrink-0">
        N
      </div>
    </div>
  );
}
